import { shareEqual } from '../collaboration/structuralSharing';
/**
 * Editor state.
 *
 * Deliberately a plain observable rather than React state: the plan canvas and the 3D
 * scene both mutate on every pointer move, and routing that through the reconciler is
 * exactly the workload React is worst at. Components subscribe with `useStore`.
 */
import { catalogProjectId, setCatalogProject, type WebState } from './catalogApi';
import type { Item, Level, Opening, Scene, Surface, Vec2, Wall } from '../core/hp/types';
import type { WallMode } from './wallMode';
import { DEFAULT_WINDOW_VIEW } from './windowViews';
import { defaultWallView, stepWallView, type WallViewTarget } from './wallPlan';
import { stepCeiling } from './ceilingStepper';
import { buildingShowsIn3D, copyFloor, showsIn3D, staysOnNewFloor } from './floors';
import { freeFloorSlots, upperInsertIndex, withoutRoofRooms } from './roofRooms';
import { levelLabel } from './levelTitles';
export { levelLabel };
import { leadsUp } from '../core/geometry/stairwells';
import * as geometry from '../core/geometry/edit';
import type { EditResult, EndpointConnection, OpeningProperties, VertexSnapTarget, WallProperties } from '../core/geometry/edit';
import { insertOpening, rectangularRoom, type CatalogOpening, type OpeningKind } from './planConstruction';
import { connectedRoomFaces, duplicatedOpening, filledHiddenWall, isContourWall, isFreeStandingWall, splitWallAt, trimRoomWalls, withRoomPlinths, withRoomTrims, withWallHidden, withWallPlinth, withWallTrim, type Trim } from './contextCommands';
import { hungCurtain, withOpeningCurtains, type CurtainModel } from './curtains';
import { newAttic, takesAttic } from './attic';
import { clampWallThickness, defaultWallThickness } from '../core/hp/defaults';
import { repairLegacyPivots } from './legacyPivotRepair';
import { applyItemProperties } from './itemProperties';
import { replacementPlace } from './catalogPlacement';
import { editProperties, type PropertyEdit } from './propertyEdit';
import { mirrorMovedOpenings, syncOpeningTwins } from '../core/geometry/openingTwins';
import type { MaterialSlotInfo } from '../render/three/MaterialTransformPreview';
import { withTextureTransformDelta } from '../core/hp/surfaceTransform';
import { assignUniqueIds } from '../core/hp/uniqueIds';
import { scaleFactor, scalePlan } from './planScale';
import { setWallLength as typedWallLength } from './wallLength';
import { movableItems, selectedItemIds } from './selectionTap';
import { plural, t, translate } from '../i18n/I18n';
import { stackedItems, transformItemWithStack, type SurfaceProbe } from '../core/geometry/stacking';
import type { CommandKey, CommitDecision, DocumentOwner, DocumentPatch, DocumentPreview, DocumentValue, ItemIdentity, Json, LocalProposal,
  NewItemOrigin, PresenceEvent, Receipt, SharedSnapshot, SharedStoreAdapter } from '../collaboration/protocol';
import { applyDocumentPatch, applyPreviewDelta, changesNothing, diffDocument, invertDocumentPatch, LIVE_PAINT_IN_APP, livePieceRefusal, partPainted, previewDelta, rebaseItemsPatch, sameJson } from '../collaboration/patch';

export type ViewMode = 'plan' | 'scene';
export type PlanSurface = 'floor' | 'ceiling' | 'wall';
export type Tool = 'navigate' | 'wall' | 'measure' | OpeningKind | 'room';

export type Selection =
  | { kind: 'wall'; id: string; side: 'front' | 'back' }
  | { kind: 'item'; id: string }
  /** Several items at once, gathered with Shift: two or more ids, in the order they were picked. */
  | { kind: 'items'; ids: string[] }
  | { kind: 'opening'; id: string }
  | { kind: 'room'; index: number }
  | { kind: 'vertex'; wallId: string; end: 'start' | 'end' }
  /** A wall's plinth or ceiling molding, picked in 3D or on the wall plan (the app's WallPlinthController). */
  | { kind: 'trim'; wallId: string; trim: Trim }
  | null;

export type MaterialTarget =
  | { kind: 'floor'; roomIndex: number }
  | { kind: 'ceiling'; roomIndex: number }
  | { kind: 'wall'; wallId: string; side: 'front' | 'back' }
  | { kind: 'item'; itemId: string; slot: string }
  /** A part of a door's or window's model (`slot` '*' for all of it); the door's copy on the other room's wall follows. */
  | { kind: 'opening'; openingId: string; slot: string }
  /** A wall's plinth or ceiling molding; «all» dresses every one of its kind in the room. */
  | { kind: 'trim'; wallId: string; trim: Trim }
  | null;

export interface CatalogEntry {
  id: string;
  title: string;
  section: string;
  /** Catalog size in metres. */
  size: [number, number, number];
  thumb?: string;
  tags: string[];
  converted: boolean;
}

export interface PropertyPreview {
  owner?: object;
  target: string;
  projectId: string;
  levelIndex: number;
  baseScene: Scene;
  scene: Scene;
}
/** What an inspector's draft belongs to: an object by its id, a room by its index. */
const propertyTarget = (selection: Selection) => selection && 'id' in selection ? selection.kind + ':' + selection.id
  : selection?.kind === 'room' ? 'room:' + selection.index : selection?.kind === 'trim' ? `trim:${selection.wallId}:${selection.trim}` : null;
/** The inspector edit's key, as `propertyTarget` names its selection. */
const editTarget = (edit: PropertyEdit) => edit.kind === 'trim' ? `trim:${edit.id}:${edit.trim}` : edit.kind + ':' + edit.id;

/** What the person may change: their own project, only the items of the app's live room, or nothing. */
export type EditScope = 'all' | 'items' | 'none';

export interface EditorState {
  scene: Scene | null;
  propertiesPreview: PropertyPreview | null;
  activeLevelIndex: number;
  projectId: string;
  projectName: string;
  loading: boolean;
  /** What the loading pill says while a project's models are being prepared. */
  loadingStatus: string | null;
  /** The catalog queue's last word on every model the editor has asked about, by model id. */
  modelStates: Record<string, WebState>;
  error: string | null;
  /** A passing word under the header (FloorNotice): what a gesture or a command left as it was; `key` tells a repeat apart. */
  notice: { text: string; key: number; icon?: 'padlock' } | null;
  /** Timestamp of the most recent browser-local scene snapshot. */
  localSavedAt: number | null;
  /** A failed snapshot must never be presented as a saved current document. */
  saveError: string | null;
  /**
   * `none`: an invitation's project, opened only to look at (`loadScene(…, { edit: 'none' })`): every change, selection
   * and local save is refused quietly, so nothing of the owner's project is kept in this browser.
   */
  edit: EditScope;

  mode: ViewMode;
  tool: Tool;
  planSurface: PlanSurface;
  /** The wall face the wall plan shows, when `planSurface` is `wall` (`src/state/wallPlan.ts`). */
  wallView: WallViewTarget | null;
  firstPerson: boolean;
  /** Whole walls, the orbit cutaway, or every wall at waist height. */
  wallMode: WallMode;
  showCeiling: boolean;
  /** What a drawn wall is built with, in metres: the locale's standard until the user steps it. */
  wallThickness: number;
  showDimensions: boolean;
  isDragging: boolean;
  isCameraDragging: boolean;
  resizeMode: boolean;
  /** «Изменить масштаб плана» is on: the plan's lengths and areas take real values (`planScale.ts`), for this floor or all. */
  planScale: { everyFloor: boolean } | null;
  /** The ruler's lines on the floor plan while it is armed (the app's measuring mode): not the project's, gone with the tool. */
  measurements: Array<{ from: Vec2; to: Vec2 }>;
  /** «Повернуть план» is on: the floor plan has the app's handle that turns the whole building (`planRotation.ts`). */
  planRotation: boolean;
  selectionMaterialSlot: string | null;
  selectionMaterialSlots: MaterialSlotInfo[];
  materialTransformPreview: { target: NonNullable<MaterialTarget>; baseline: Surface; draft: Surface; slots?: MaterialSlotInfo[] } | null;

  selection: Selection;
  /** Where the selection was made, in canvas pixels — the context bar follows it. */
  selectionAnchor: { x: number; y: number } | null;
  /**
   * Where the user last touched the floor — where a new item is added (the app's rule).
   * It belongs to one floor of one project, so opening another clears it: a coordinate
   * from a different plan would drop furniture out of sight.
   */
  lastFloorPoint: Vec2 | null;

  showCatalog: boolean;
  catalogSection: string | null;
  catalogQuery: string;
  /** The door, window or arch the open catalog chooses curtains for («Выбрать шторы», the app's AddCurtains), else null. */
  curtainsFor: string | null;
  showProperties: boolean;
  materialTarget: MaterialTarget;
  showVariations: boolean;
  showLighting: boolean;
  showGallery: boolean;
  /** Lite's camera mode over the 3D view: photo / AI render. */
  cameraMode: boolean;
  /**
   * The app's demo walkthrough («Демонстрационный обзор», EditorRoomMode.Demo): the walk over the whole window for a
   * presentation, the editor's controls put away, nothing picked, each doorway in view offering to go through.
   */
  walkthrough: boolean;
  /** The minimap popup (the app's MinimapPopup). */
  showMinimap: boolean;
  /** "Hide other rooms": the index of the only room shown on the active level, else null. */
  isolatedRoom: number | null;
  /** A room wall whose removal waits for «Удалить стену» or «Скрыть стену», as the app asks (RemoveWallSegment). */
  wallRemoval: string | null;
  /** A room whose removal or clone waits for its answer, as the app asks (RemoveRoom, CloneRoom). */
  roomAction: { kind: 'remove' | 'clone'; index: number } | null;
  /** The view out of the windows (`src/state/windowViews.ts`): a view's id, or «none» for the plain background. */
  windowView: string;
  /** How far that view is turned, in whole degrees clockwise on the plan. */
  windowViewRotation: number;
  /** Whether the chosen view's pictures are coming, in or failed, as the 3D view reports. */
  windowViewStatus: 'idle' | 'loading' | 'ready' | 'error';
  /** The «Вид из окна» sheet. */
  showWindowViews: boolean;

  stats: Record<string, number>;
}

const INITIAL: EditorState = {
  scene: null,
  propertiesPreview: null,
  activeLevelIndex: 0,
  projectId: '',
  projectName: '',
  loading: true,
  loadingStatus: null,
  modelStates: {},
  error: null,
  notice: null,
  localSavedAt: null,
  saveError: null,
  edit: 'all',
  mode: 'scene',
  tool: 'navigate',
  planSurface: 'floor',
  wallView: null,
  firstPerson: false,
  wallMode: 'full',
  showCeiling: false,
  wallThickness: 0.2,
  showDimensions: true,
  isDragging: false,
  isCameraDragging: false,
  resizeMode: false,
  planScale: null,
  measurements: [],
  planRotation: false,
  selectionMaterialSlot: null,
  selectionMaterialSlots: [],
  materialTransformPreview: null,
  selection: null,
  selectionAnchor: null,
  lastFloorPoint: null,
  showCatalog: false,
  catalogSection: null,
  catalogQuery: '',
  curtainsFor: null,
  showProperties: false,
  materialTarget: null,
  showVariations: false,
  showLighting: false,
  showGallery: false,
  cameraMode: false,
  walkthrough: false,
  showMinimap: false,
  isolatedRoom: null,
  wallRemoval: null,
  roomAction: null,
  windowView: DEFAULT_WINDOW_VIEW,
  windowViewRotation: 0,
  windowViewStatus: 'idle',
  showWindowViews: false,
  stats: {},
};

type Listener = () => void;
const WALL_THICKNESS_KEY = 'hp.wall-thickness';
const ITEMS_ONLY = '#WebLiveItemsOnly', ONLY_IN_APP = '#WebLiveOnlyInApp';
/** How far Duplicate puts a copy from its source, in metres. */
const COPY_SHIFT = .3;
/** The least a wall piece's copy moves, the app's own (`GetDuplicateFurnitureOffsetPoint`): never on its source. */
const COPY_MIN_SHIFT = .15;
function storedWallThickness(): number | null {
  try { const value = Number(localStorage.getItem(WALL_THICKNESS_KEY)); return value > 0 ? clampWallThickness(value) : null; } catch { return null; }
}
export interface ItemCreationEvent { projectId: string; levelIndex: number; ids: readonly string[] }
export interface DocumentScope { projectId: string; epoch: number; levelIndex: number; scopeVersion: number; revision: number }
export interface DocumentCommit { scope: DocumentScope; commandId?: string; turnId?: string; undoable?: false }
export type DocumentConflict = 'scope_conflict' | 'revision_conflict' | 'busy' | 'cancelled';
type HistoryEntry = { scene: Scene; levelIndex: number; shared?: DocumentPatch };

export class Store {
  private state: EditorState = INITIAL;
  private listeners = new Set<Listener>();
  private past: HistoryEntry[] = [];
  private future: HistoryEntry[] = [];
  private clipboard: { projectId: string; picked: string[]; sources: { item: Item; baseId: string }[] } | null = null;
  private strokeBase: HistoryEntry | null = null;
  private savePending = false;
  private creationListeners = new Set<(event: ItemCreationEvent) => void>();
  private documentListeners = new Set<(event: DocumentCommit) => void>();
  private epoch = 0;
  private scopeVersion = 0;
  private revision = 0;
  private loadedProjectId: string | null = null;
  private documentOwner: DocumentOwner = {};
  private initialFloorPoint: Vec2 | null = null;
  private shared: SharedStoreAdapter | null = null;
  private previewListeners = new Set<(preview: DocumentPreview | null) => void>();
  private sharedSnapshot: SharedSnapshot | null = null;
  /** `selection`: the person's selection before the edit, given back if the room refuses the pieces the edit added and selected. */
  private sharedPending: { proposal: LocalProposal; patch: DocumentPatch; history?: { kind: 'undo' | 'redo'; entries: HistoryEntry[] }; selection: Selection } | null = null;
  private commandDecisions = new Map<string, Promise<CommitDecision>>();
  private remotePreviews: PresenceEvent[] = [];
  private gesture: { id: string; before: DocumentValue; scope: DocumentScope; layoutVersion: number } | null = null;
  private endingDraft = false;
  private lastPreview: DocumentPreview | null = null;
  private presentationCache: { base: Scene; previews: PresenceEvent[]; gesture: Store['gesture']; layout: number | undefined; scene: Scene } | null = null;

  constructor(private readonly isolated = false) {}

  attachShared(adapter: SharedStoreAdapter | null): void {
    if (this.isolated) return;
    if (!adapter && this.sharedSnapshot && this.state.scene && this.loadedProjectId === this.state.projectId) this.publish({ scene: this.sharedSnapshot.scene,
      projectName: this.sharedSnapshot.name, isDragging: false, propertiesPreview: null, materialTransformPreview: null });
    this.shared = adapter;
    this.sharedPending = null; this.sharedSnapshot = null; this.remotePreviews = []; this.gesture = null;
    this.past = []; this.future = []; this.lastLabel = '';
  }

  get isShared(): boolean { return !!this.shared; }
  isSharedAdapter(adapter: SharedStoreAdapter): boolean { return this.shared === adapter; }
  lookupSharedReceipt(key: CommandKey, request: Json): Promise<Receipt | null> { return this.shared?.replay?.(key, request) ?? Promise.resolve(null); }

  async awaitDocumentCommand(commandId: string): Promise<CommitDecision> {
    return this.commandDecisions.get(commandId) ?? { status: 'unchanged', revision: this.revision };
  }

  async adoptDocumentDraftAccepted(draft: Store, scope: DocumentScope, owner: DocumentOwner, signal?: AbortSignal): Promise<CommitDecision> {
    if (this.shared) {
      if (this.state.edit !== 'all') return { status: 'rejected', code: 'busy' };
      const conflict = this.documentConflict(scope, signal);
      if (conflict) return { status: 'rejected', code: conflict === 'revision_conflict' ? 'conflict' : conflict };
      const before = this.getCommittedDocument(), after = draft.getCommittedDocument();
      if (!before || !after) return { status: 'rejected', code: 'scope_conflict' };
      const created = after.scene.levels[scope.levelIndex].items.filter(item => !before.scene.levels[scope.levelIndex].items.some(old => old.id === item.id)).map(item => item.id);
      const result = this.scheduleShared(before, after, scope, owner, created, '', true, 'edit');
      if (!signal) return result;
      return new Promise(resolve => {
        const aborted = () => resolve({ status: 'pending', commandId: owner.commandId ?? this.sharedPending?.proposal.owner.commandId ?? '' });
        signal.addEventListener('abort', aborted, { once: true });
        void result.then(resolve).finally(() => signal.removeEventListener('abort', aborted));
        if (signal.aborted) aborted();
      });
    }
    const revision = this.revision, conflict = this.adoptDocumentDraft(draft, scope, owner, signal);
    if (conflict) return { status: 'rejected', code: conflict === 'revision_conflict' ? 'conflict' : conflict };
    return { status: this.revision === revision ? 'unchanged' : 'committed', revision: this.revision };
  }

  acceptShared(snapshot: SharedSnapshot, owner?: DocumentOwner): void {
    if (!this.shared || this.sharedSnapshot && (snapshot.documentId !== this.sharedSnapshot.documentId || snapshot.revision < this.sharedSnapshot.revision)) return;
    const previous = this.sharedSnapshot;
    const changed = !!previous && snapshot.revision > previous.revision;
    const ownAcknowledgement = !!owner?.commandId && this.sharedPending?.proposal.owner.commandId === owner.commandId;
    if (previous) snapshot = { ...snapshot, scene: shareEqual(previous.scene, snapshot.scene) };
    this.sharedSnapshot = snapshot;
    const draft = !!(this.strokeBase || this.state.materialTransformPreview || this.state.propertiesPreview || this.sharedPending && !ownAcknowledgement);
    if (changed && owner && snapshot.revision > previous!.revision + 1) this.withDocumentOwner({}, () => this.publish({}, [], true));
    // A co-author's floor added or removed before this one leaves the person on the floor they were looking at.
    const shown = this.state.scene?.levels[this.state.activeLevelIndex]?.uniqueId;
    const kept = shown ? snapshot.scene.levels.findIndex(level => level.uniqueId === shown) : -1;
    // In the live room a refusal stays until the person's next edit or dismissal: a co-author's edit landing says nothing of it.
    const cleared = ownAcknowledgement || this.state.edit !== 'items' ? { saveError: null } : {};
    this.withDocumentOwner(owner ?? {}, () => this.publish({ ...(!draft ? { scene: snapshot.scene, projectName: snapshot.name,
      activeLevelIndex: kept >= 0 ? kept : Math.min(this.state.activeLevelIndex, snapshot.scene.levels.length - 1) } : {}), ...cleared }, [], changed || ownAcknowledgement,
      ownAcknowledgement ? this.sharedPending!.proposal.undoable : true));
    this.setRemotePreviews(this.remotePreviews.filter(value => value.preview && value.preview.baseRevision >= snapshot.revision && value.floor.layoutVersion === snapshot.layoutVersion));
  }

  resolveSharedCommand(commandId: string, decision: CommitDecision): void {
    const pending = this.sharedPending;
    if (!pending || pending.proposal.owner.commandId !== commandId || decision.status === 'pending') return;
    this.sharedPending = null;
    this.commandDecisions.set(commandId, Promise.resolve(decision));
    const { proposal, patch, history } = pending, current = this.captureDocumentScope(), live = this.state.edit === 'items';
    if (!current || current.projectId !== proposal.scope.projectId || current.epoch !== proposal.scope.epoch) return;
    if (decision.status === 'committed') {
      const stored = decision.patch ?? patch;
      const inverse = invertDocumentPatch(stored, { revision: decision.revision, layoutVersion: stored.layoutVersion + (stored.kind === 'replace' ? 1 : 0) });
      // The room gave the edit back unchanged: a step for it would undo nothing and stand in front of the steps behind it.
      if (live && changesNothing(stored)) { if (history) this.passSteps(history.kind, history.entries); }
      else if (inverse.ok && proposal.undoable) {
        const entry = { scene: proposal.before.scene, levelIndex: proposal.scope.levelIndex, shared: inverse.value };
        if (history?.kind === 'undo') { for (const value of history.entries) if (this.past.at(-1) === value) this.past.pop(); this.future.push(entry); }
        else if (history?.kind === 'redo') { for (const value of history.entries) if (this.future.at(-1) === value) this.future.pop(); this.pushPast(entry); }
        else { this.pushPast(entry); this.future = []; }
      }
      if (current.levelIndex === proposal.scope.levelIndex) this.emitItemCreations(proposal.createdItemIds);
    } else if (history && decision.status === 'unchanged' && live) this.passSteps(history.kind, history.entries);
    else if (history && decision.status === 'rejected' && live && (decision.code === 'invalid' || decision.code === 'conflict')) this.forgetSteps(history.entries);
    const snapshot = this.sharedSnapshot, selectedIds = selectedItemIds(this.state.selection);
    const lostSelection = decision.status === 'rejected' && selectedIds.length > 0 && selectedIds.every(id => proposal.createdItemIds.includes(id));
    this.publish({ ...(snapshot ? { scene: snapshot.scene, projectName: snapshot.name } : {}), ...(lostSelection ? { selection: pending.selection } : {}),
      saveError: decision.status === 'rejected' ? this.sharedError(decision.code, decision.reason, decision.args)
        : decision.notice ? translate(decision.notice, ...(decision.args ?? [])) : null, isDragging: false });
    if (decision.status === 'committed') this.saveLocal();
  }

  setRemotePreviews(previews: PresenceEvent[]): void {
    // Cursor, camera, timestamp and sequence updates never invalidate the document renderer.
    if (this.remotePreviews.length === previews.length && previews.every((value, index) => {
      const old = this.remotePreviews[index];
      return old.userId === value.userId && old.clientId === value.clientId && old.floor.slot === value.floor.slot
        && old.floor.layoutVersion === value.floor.layoutVersion && sameJson(old.preview, value.preview);
    })) return;
    this.remotePreviews = previews;
    this.state = { ...this.state };
    for (const fn of this.listeners) { try { fn(); } catch { /* presentation observer */ } }
  }
  subscribeDocumentPreviews = (fn: (preview: DocumentPreview | null) => void): (() => void) => {
    this.previewListeners.add(fn);
    return () => { this.previewListeners.delete(fn); };
  };

  getCommittedDocument(): DocumentValue | null {
    if (this.sharedSnapshot) return { scene: this.sharedSnapshot.scene, name: this.sharedSnapshot.name };
    const scene = this.strokeBase?.scene ?? this.state.scene;
    return scene ? { scene, name: this.state.projectName } : null;
  }

  private sharedError(code: string, reason?: string, args: readonly string[] = []): string {
    if (reason) return translate(reason, ...args);
    return this.shared?.explain?.(code) ?? (code === 'forbidden' ? t('#StoreAccessClosed') : code === 'conflict' ? t('#StoreSharedConflict')
      : code === 'invalid' ? t('#StoreSharedInvalid') : t('#StoreSharedBusy'));
  }

  private refuseShared(code: 'busy' | 'conflict' | 'invalid' | 'scope_conflict' = 'busy', reason?: string): Promise<CommitDecision> {
    const decision = Promise.resolve<CommitDecision>({ status: 'rejected', code, ...(reason ? { reason } : {}) });
    if (this.documentOwner.commandId) this.commandDecisions.set(this.documentOwner.commandId, decision);
    this.publish({ saveError: this.sharedError(code, reason) });
    return decision;
  }

  /** In the app's live room the web changes items alone: anything else is refused here, before any question opens. */
  private refusedInRoom(reason = ITEMS_ONLY): boolean {
    if (this.state.edit !== 'items') return false;
    void this.refuseShared('invalid', reason);
    return true;
  }

  private scheduleShared(before: DocumentValue, after: DocumentValue, scope: DocumentScope, owner: DocumentOwner, createdItemIds: string[],
    label: string, undoable: boolean, kind: LocalProposal['kind'], gestureId?: string, history?: { kind: 'undo' | 'redo'; entries: HistoryEntry[] }, historyPatch?: DocumentPatch): Promise<CommitDecision> {
    if (!this.shared?.writable || this.sharedPending) return this.refuseShared();
    this.shared.readArrived?.();
    if (!this.shared?.writable) return this.refuseShared();
    const context = this.sharedSnapshot;
    if (!context) return this.refuseShared('scope_conflict');
    if (this.gesture && this.gesture.layoutVersion !== context.layoutVersion) return this.refuseShared('scope_conflict');
    const diff = historyPatch ? { ok: true as const, value: historyPatch } : diffDocument(before, after,
      { baseRevision: context.revision, layoutVersion: context.layoutVersion, replace: kind === 'replace' });
    if (!diff.ok) return this.refuseShared(diff.code);
    if (!diff.value) {
      const result = Promise.resolve<CommitDecision>({ status: 'unchanged', revision: context.revision });
      if (owner.commandId) this.commandDecisions.set(owner.commandId, result);
      return result;
    }
    // Mirror and clearing the plan arrive as plain level commits: only the change itself tells them from an item edit.
    const outside = this.state.edit === 'items' ? itemsOnlyRefusal(diff.value) : null;
    if (outside) { if (history) this.forgetSteps(history.entries); return this.refuseShared('invalid', outside); }
    const proposal: LocalProposal = { before, after, scope, owner: { ...owner, commandId: owner.commandId ?? crypto.randomUUID() }, createdItemIds, label, undoable, kind, gestureId, patch: diff.value };
    this.sharedPending = { proposal, patch: diff.value, history, selection: this.state.selection };
    this.publish({ scene: after.scene, projectName: after.name, saveError: null, propertiesPreview: null, materialTransformPreview: null });
    const promise = this.shared.submit(proposal).then(decision => { this.resolveSharedCommand(proposal.owner.commandId!, decision); return decision; }, () => ({ status: 'pending' as const, commandId: proposal.owner.commandId! }));
    this.commandDecisions.set(proposal.owner.commandId!, promise);
    while (this.commandDecisions.size > 256) this.commandDecisions.delete(this.commandDecisions.keys().next().value!);
    return promise;
  }

  private sharedHistory(kind: 'undo' | 'redo', count = 1): void {
    if (this.strokeBase) { if (kind === 'undo') this.cancelStroke(); return; }
    if (this.documentBusy) { void this.refuseShared(); return; }
    this.shared?.readArrived?.();
    const entries = (kind === 'undo' ? this.past : this.future).slice(-Math.max(1, Math.floor(count))).reverse();
    const before = this.getCommittedDocument(), scope = this.captureDocumentScope();
    if (!entries.length || !before || !scope || !this.sharedSnapshot) return;
    // In the app's live room a step carries its own changes onto what the room holds now, not a guard on everything around them.
    const live = this.state.edit === 'items', taken: DocumentPatch[] = [];
    let after = before, replace = false;
    for (const entry of entries) {
      const stored = entry.shared;
      if (!stored || stored.layoutVersion !== this.sharedSnapshot.layoutVersion || stored.kind === 'replace' && stored.baseRevision !== this.sharedSnapshot.revision) { if (live) this.forgetSteps([entry]); void this.refuseShared('conflict'); return; }
      const rebased = live ? rebaseItemsPatch(after, stored, true) : { ok: true as const, value: stored };
      if (!rebased.ok) { if (live) this.forgetSteps([entry]); void this.refuseShared(rebased.code); return; }
      if (!rebased.value) continue;
      const applied = applyDocumentPatch(after, rebased.value);
      if (!applied.ok) { if (live) this.forgetSteps([entry]); void this.refuseShared(applied.code); return; }
      after = applied.value; replace ||= rebased.value.kind === 'replace'; taken.push(rebased.value);
    }
    if (!taken.length) { this.passSteps(kind, entries); return; }
    const patch = taken.length === 1 ? { ok: true as const, value: taken[0] } : diffDocument(before, after,
      { baseRevision: this.sharedSnapshot.revision, layoutVersion: this.sharedSnapshot.layoutVersion, replace });
    if (!patch.ok) { if (live) this.forgetSteps(entries); void this.refuseShared(patch.code); return; }
    if (patch.value) void this.scheduleShared(before, after, scope, this.documentOwner, [], '', true, kind, undefined, { kind, entries }, patch.value);
  }

  /** Steps the live room refused for good leave the undo and redo stacks, so the steps behind them come within reach; elsewhere a refused step stays to try again. */
  private forgetSteps(entries: readonly HistoryEntry[]): void {
    this.past = this.past.filter(entry => !entries.includes(entry));
    this.future = this.future.filter(entry => !entries.includes(entry));
  }

  /** Steps the room already holds are done: each moves to the other stack, with nothing sent and nothing said. */
  private passSteps(kind: 'undo' | 'redo', entries: readonly HistoryEntry[]): void {
    const accepted = { revision: this.sharedSnapshot?.revision ?? 0, layoutVersion: this.sharedSnapshot?.layoutVersion ?? 0 };
    this.forgetSteps(entries);
    for (const entry of entries) {
      const inverse = entry.shared ? invertDocumentPatch(entry.shared, accepted) : null;
      if (!inverse?.ok) continue;
      const done = { scene: this.sharedSnapshot?.scene ?? entry.scene, levelIndex: entry.levelIndex, shared: inverse.value };
      if (kind === 'undo') this.future.push(done); else this.pushPast(done);
    }
    this.state = { ...this.state, saveError: null };
    for (const fn of this.listeners) { try { fn(); } catch { /* presentation observer */ } }
  }

  private emitPreview(): void {
    if (this.isolated || !this.previewListeners.size) return;
    const pending = this.sharedPending?.proposal;
    if (pending?.gestureId) {
      const slot = pending.scope.levelIndex, current = pending.after.scene.levels[slot];
      if (this.lastPreview?.current === current && this.lastPreview.gestureId === pending.gestureId) return;
      const preview = { gestureId: pending.gestureId, baseline: pending.before.scene.levels[slot], current, slot };
      this.lastPreview = preview;
      for (const fn of this.previewListeners) { try { fn(preview); } catch { /* transient publication */ } }
      return;
    }
    const scene = this.state.scene, slot = this.state.activeLevelIndex;
    const active = !!(this.strokeBase || this.state.propertiesPreview || this.state.materialTransformPreview);
    if (!active || !scene) {
      if (this.lastPreview) { this.lastPreview = null; for (const fn of this.previewListeners) fn(null); }
      if (!this.strokeBase) this.gesture = null;
      return;
    }
    const scope = this.captureDocumentScope();
    if (!scope) return;
    if (!this.gesture) this.gesture = { id: crypto.randomUUID(), before: { scene: this.state.propertiesPreview?.baseScene ?? scene, name: this.state.projectName },
      scope, layoutVersion: this.sharedSnapshot?.layoutVersion ?? 0 };
    let current = this.state.propertiesPreview?.scene ?? scene;
    if (this.state.materialTransformPreview) {
      const draft = this.createDocumentDraft();
      draft.endMaterialTransform(true);
      current = draft.state.scene ?? current;
    }
    const preview = { gestureId: this.gesture.id, baseline: this.gesture.before.scene.levels[slot], current: current.levels[slot], slot };
    if (!preview.baseline || !preview.current || this.lastPreview?.current === preview.current) return;
    this.lastPreview = preview;
    for (const fn of this.previewListeners) { try { fn(preview); } catch { /* transient publication */ } }
  }

  captureDocumentScope(): DocumentScope | null {
    return this.state.scene && !this.state.loading && this.loadedProjectId === this.state.projectId ? { projectId: this.state.projectId, epoch: this.epoch, levelIndex: this.state.activeLevelIndex,
      scopeVersion: this.scopeVersion, revision: this.revision } : null;
  }

  subscribeDocumentCommits = (fn: (event: DocumentCommit) => void): (() => void) => {
    this.documentListeners.add(fn);
    return () => { this.documentListeners.delete(fn); };
  };

  get stroking(): boolean { return !!this.strokeBase; }
  get documentBusy(): boolean { return !!(this.strokeBase || this.state.materialTransformPreview || this.state.propertiesPreview || this.sharedPending || this.shared && !this.shared.writable); }
  /** Settles once the shared edit still waiting on its answer, if any, has it: an answer that changes nothing on screen tells no listener. */
  async documentSettled(): Promise<void> {
    const pending = this.sharedPending && this.commandDecisions.get(this.sharedPending.proposal.owner.commandId!);
    if (pending) await pending.catch(() => undefined);
  }

  documentConflict(scope: DocumentScope | null, signal?: AbortSignal): DocumentConflict | null {
    if (signal?.aborted) return 'cancelled';
    const current = this.captureDocumentScope();
    if (!scope || !current || scope.projectId !== current.projectId || scope.epoch !== current.epoch
      || scope.levelIndex !== current.levelIndex || scope.scopeVersion !== current.scopeVersion) return 'scope_conflict';
    if (scope.revision !== current.revision) return 'revision_conflict';
    return this.documentBusy ? 'busy' : null;
  }

  withDocumentOwner<T>(owner: DocumentOwner, work: () => T): T {
    const previous = this.documentOwner;
    this.documentOwner = owner;
    try { return work(); } finally { this.documentOwner = previous; }
  }

  createDocumentDraft(): Store {
    const draft = new Store(true);
    draft.state = { ...this.state, scene: structuredClone(this.state.scene) };
    draft.loadedProjectId = this.loadedProjectId;
    draft.initialFloorPoint = this.state.lastFloorPoint;
    draft.surfaceProbe = this.surfaceProbe;
    return draft;
  }

  adoptDocumentDraft(draft: Store, scope: DocumentScope, owner: Pick<DocumentCommit, 'commandId' | 'turnId'>, signal?: AbortSignal): DocumentConflict | null {
    if (this.shared || this.state.edit !== 'all') return 'busy';
    const conflict = this.documentConflict(scope, signal);
    if (conflict) return conflict;
    const scene = draft.state.scene;
    if (!scene || JSON.stringify(scene) === JSON.stringify(this.state.scene)) return null;
    const before = new Set(this.level?.items.map(item => item.id));
    const created = scene.levels[scope.levelIndex]?.items.filter(item => !before.has(item.id)).map(item => item.id) ?? [];
    const selection = this.state.selection, level = scene.levels[scope.levelIndex];
    const removed = selection && (selection.kind === 'item' ? !level?.items.some(item => item.id === selection.id)
      : selection.kind === 'opening' ? !level?.openings.some(item => item.id === selection.id)
      : selection.kind === 'wall' ? !level?.walls.some(item => item.id === selection.id)
      : selection.kind === 'vertex' ? !level?.walls.some(item => item.id === selection.wallId)
      : selection.kind === 'trim' ? !level?.walls.some(item => item.id === selection.wallId && item[selection.trim])
      : selection.kind === 'items' ? !selection.ids.every(id => level?.items.some(item => item.id === id))
      : !level?.rooms.some(room => room.index === selection.index));
    this.withDocumentOwner(owner, () => {
      this.lastLabel = '';
      this.pushPast({ scene: this.state.scene!, levelIndex: scope.levelIndex });
      this.future = [];
      this.publish({ scene: assignUniqueIds(this.state.scene!, scene), ...(draft.state.lastFloorPoint !== draft.initialFloorPoint ? { lastFloorPoint: draft.state.lastFloorPoint } : {}),
        ...(removed ? { selection: null, selectionAnchor: null } : {}) }, created, true);
      this.requestSave();
    });
    return null;
  }

  get(): EditorState { return this.state; }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  };

  /** Presentation events are neither scene data nor history: restore/redo never replays them. */
  subscribeItemCreations = (fn: (event: ItemCreationEvent) => void): (() => void) => {
    this.creationListeners.add(fn);
    return () => { this.creationListeners.delete(fn); };
  };

  private emitItemCreations(ids: readonly string[]): void {
    if (!ids.length) return;
    const event = { projectId: this.state.projectId, levelIndex: this.state.activeLevelIndex, ids };
    for (const listener of this.creationListeners) { try { listener(event); } catch { /* presentation cannot reject an accepted edit */ } }
  }

  /**
   * Adds `item` to the floor in view. A flight of stairs that reaches the floor above leads up to it (`leadsUp`), and the
   * floor above has a stairwell over it; on the top floor there is no floor above yet, so it is added with the flight, in
   * the same undo step, the way «Добавить этаж» adds one, and the floor in view stays. Returns the index of the floor
   * added, or null.
   */
  insertItem(item: Item): number | null {
    const scene = this.state.scene, index = this.state.activeLevelIndex, level = scene?.levels[index];
    if (!scene || !level || level.items.some(existing => existing.id === item.id)) return null;
    if (index === scene.levels.length - 1 && upperInsertIndex(scene.levels) === scene.levels.length && leadsUp(item, level) && freeFloorSlots(scene.levels) > 0) {
      const placed = { ...scene, levels: scene.levels.map((value, i) => i === index ? { ...value, items: [...value.items, structuredClone(item)] } : value) };
      const { scene: next, index: added } = withLevel(placed, 'above');
      this.commit('', () => next, [item.id]);
      return this.state.scene !== scene ? added : null;
    }
    this.commitLevel('', value => ({ ...value, items: [...value.items, structuredClone(item)] }), [item.id]);
    return null;
  }

  /** Patch state without touching the undo stack (UI-only changes). */
  set(patch: Partial<EditorState>): void {
    let renamed = !!this.state.scene && patch.projectName !== undefined && patch.projectName !== this.state.projectName
      && !('scene' in patch) && (!('projectId' in patch) || patch.projectId === this.state.projectId);
    // A viewer keeps the owner's name and has no photo (its AI render makes a device account); the gallery opens to look at.
    if (this.state.edit === 'none' && (renamed || patch.cameraMode)) {
      const { cameraMode: _camera, ...rest } = patch;
      patch = renamed ? { ...rest, projectName: this.state.projectName } : rest;
      renamed = false;
    }
    if (renamed && this.shared) {
      const before = this.getCommittedDocument(), scope = this.captureDocumentScope();
      if (before && scope) void this.scheduleShared(before, { ...before, name: patch.projectName! }, scope, this.documentOwner, [], '', false, 'edit');
      const { projectName: _name, ...rest } = patch;
      this.publish(rest); return;
    }
    this.publish(patch, [], renamed, false);
  }

  private publish(patch: Partial<EditorState>, createdItemIds: readonly string[] = [], committed = false, undoable = true): void {
    // The ruler's lines go with the ruler.
    if ((patch.tool ?? this.state.tool) !== 'measure' && this.state.measurements.length && !('measurements' in patch)) patch = { ...patch, measurements: [] };
    // The plan's turn is the floor plan's, with nothing else armed: leaving it, a tool or a selection ends it.
    if (this.state.planRotation && !('planRotation' in patch) && ((patch.mode ?? this.state.mode) !== 'plan' || (patch.planSurface ?? this.state.planSurface) !== 'floor'
      || (patch.tool ?? this.state.tool) !== 'navigate' || ('selection' in patch && patch.selection) || patch.planScale)) patch = { ...patch, planRotation: false };
    // The demo walkthrough is a walk: going to the plan or the orbit ends it.
    if (this.state.walkthrough && !('walkthrough' in patch) && (patch.mode === 'plan' || patch.firstPerson === false)) patch = { ...patch, walkthrough: false };
    // A plinth or a molding is picked in 3D and on the wall plan; the floor and ceiling plans do not draw it, and it lets go there.
    const selected = 'selection' in patch ? patch.selection : this.state.selection;
    if (selected?.kind === 'trim' && (patch.mode ?? this.state.mode) === 'plan' && (patch.planSurface ?? this.state.planSurface) !== 'wall') patch = { ...patch, selection: null, selectionAnchor: null };
    const hadDraft = !!(this.state.propertiesPreview || this.state.materialTransformPreview || this.state.isDragging);
    const selectionChanged = 'selection' in patch && JSON.stringify(patch.selection) !== JSON.stringify(this.state.selection);
    const projectChanged = 'projectId' in patch && patch.projectId !== this.state.projectId;
    const propertyScopeChanged = ('selection' in patch && propertyTarget(patch.selection ?? null) !== propertyTarget(this.state.selection))
      || projectChanged || ('activeLevelIndex' in patch && patch.activeLevelIndex !== this.state.activeLevelIndex);
    if (propertyScopeChanged || ('scene' in patch && patch.scene !== this.state.scene)) patch = { ...patch, propertiesPreview: null };
    if (projectChanged && this.savePending) this.saveLocal();
    // The wall plan belongs to the 2D mode of one floor and to a wall that still exists: leaving
    // for 3D, changing floors or losing the wall (a delete, an undo) lands on the floor plan.
    const surface = patch.planSurface ?? this.state.planSurface, levelIndex = patch.activeLevelIndex ?? this.state.activeLevelIndex;
    if (surface === 'wall') {
      const wallView = 'wallView' in patch ? patch.wallView : this.state.wallView;
      const level = (patch.scene ?? this.state.scene)?.levels[levelIndex];
      const kept = wallView && level?.walls.some(wall => wall.id === wallView.wallId && !wall.hidden);
      const leaving = ('mode' in patch && patch.mode === 'scene') || (levelIndex !== this.state.activeLevelIndex && !('wallView' in patch));
      if (leaving || !kept) patch = { ...patch, planSurface: 'floor', wallView: null };
    }
    const wallViewChanged = 'wallView' in patch && JSON.stringify(patch.wallView ?? null) !== JSON.stringify(this.state.wallView);
    const viewChanged = ('mode' in patch && patch.mode !== this.state.mode) || ('planSurface' in patch && patch.planSurface !== this.state.planSurface)
      || ('firstPerson' in patch && patch.firstPerson !== this.state.firstPerson) || wallViewChanged;
    if (selectionChanged || projectChanged) patch = { ...patch, resizeMode: false, selectionMaterialSlot: null, selectionMaterialSlots: [], materialTransformPreview: null };
    if (viewChanged) patch = { ...patch, tool: 'navigate', resizeMode: false, selectionAnchor: null, materialTransformPreview: null };
    if (patch.showProperties || patch.showCatalog || patch.showVariations || patch.showLighting || patch.showGallery || patch.cameraMode || patch.showMinimap || patch.materialTarget) patch = { ...patch, tool: 'navigate', resizeMode: false };
    if ('materialTarget' in patch && !patch.materialTarget) patch = { ...patch, materialTransformPreview: null };
    // The list a «+» asked the catalog to open on belongs to that one opening of it.
    if (patch.showCatalog === false && this.state.catalogSection !== null && !('catalogSection' in patch)) patch = { ...patch, catalogSection: null };
    // So do the curtains it chooses: they are for one opening, and the catalog goes when the selection leaves it.
    const curtainsFor = 'curtainsFor' in patch ? patch.curtainsFor : this.state.curtainsFor;
    if (curtainsFor && ((patch.showCatalog === false && !('curtainsFor' in patch))
      || (selectionChanged && !(patch.selection?.kind === 'opening' && patch.selection.id === curtainsFor)) || projectChanged)) {
      patch = { ...patch, curtainsFor: null, ...(this.state.showCatalog && patch.showCatalog !== true ? { showCatalog: false } : {}) };
    }
    if ('selection' in patch && !selectionChanged) patch.selection = this.state.selection;
    if (patch.selectionAnchor && this.state.selectionAnchor && patch.selectionAnchor.x === this.state.selectionAnchor.x && patch.selectionAnchor.y === this.state.selectionAnchor.y) patch.selectionAnchor = this.state.selectionAnchor;
    if (!committed && !Object.entries(patch).some(([key, value]) => !Object.is(this.state[key as keyof EditorState], value))) return;
    if (patch.loading === true) this.loadedProjectId = null;
    if ('activeLevelIndex' in patch && patch.activeLevelIndex !== this.state.activeLevelIndex) this.scopeVersion++;
    this.state = { ...this.state, ...patch };
    if (committed) {
      this.revision++;
      const scope = this.captureDocumentScope();
      if (scope) for (const listener of this.documentListeners) { try { listener({ scope, commandId: this.documentOwner.commandId, turnId: this.documentOwner.turnId,
        ...(!undoable ? { undoable: false as const } : {}) }); } catch { /* observers do not own the commit */ } }
    }
    // Queue presentation before a synchronous external-store subscriber can load the
    // new id. The committed document already exists, so subscribers see atomic state.
    this.emitItemCreations(createdItemIds);
    for (const fn of this.listeners) { try { fn(); } catch { /* continue notifying and saving accepted state */ } }
    this.emitPreview();
    if (hadDraft && !this.endingDraft && !this.strokeBase && !this.state.propertiesPreview && !this.state.materialTransformPreview && !this.sharedPending && this.sharedSnapshot
      && this.state.scene !== this.sharedSnapshot.scene) this.publish({ scene: this.sharedSnapshot.scene, projectName: this.sharedSnapshot.name });
  }

  /** `view` goes out with the new scene: a change to the floors says which floor is in view after it. Why nothing changed when editing is off, for the caller to show; else null. */
  commit(label: string, update: (scene: Scene) => Scene, createdItemIds: readonly string[] = [], view?: Partial<EditorState>): string | null {
    const scene = this.state.scene;
    if (!scene) return null;
    if (this.state.edit === 'none') return this.editingOff();
    const next = update(scene);
    if (next === scene) return null;
    if (this.strokeBase) { this.set({ scene: next }); return null; }
    if (this.shared) {
      const scope = this.captureDocumentScope();
      if (scope) void this.scheduleShared({ scene, name: this.state.projectName }, { scene: next, name: this.state.projectName }, scope,
        this.documentOwner, [...createdItemIds], label, true, this.replacing ? 'replace' : 'edit');
      return null;
    }
    if (!label || this.lastLabel !== label) this.pushPast({ scene, levelIndex: this.state.activeLevelIndex });
    this.lastLabel = label;
    this.future = [];
    // A whole document put back is a snapshot whose ids were given when it was committed; a draft gets its ids when adopted.
    const committed = this.replacing || this.isolated ? next : assignUniqueIds(scene, next);
    this.publish({ scene: committed, activeLevelIndex: Math.min(this.state.activeLevelIndex, Math.max(0, committed.levels.length - 1)), ...view }, createdItemIds, true);
    this.requestSave();
    return null;
  }

  /** Why the store takes no edit: the shared adapter's words for its room (the link, the ids), else view-only. */
  private editingOff(): string {
    return this.shared?.explain?.('off') ?? t('#CanViewDesc');
  }

  commitLevel(label: string, update: (level: Level) => Level, createdItemIds: readonly string[] = []): string | null {
    const index = this.state.activeLevelIndex;
    return this.commit(label, scene => {
      const level = scene.levels[index];
      if (!level) return scene;
      const next = update(level);
      if (next === level) return scene;
      if (this.strokeBase?.scene.levels[index] === next) return this.strokeBase.scene;
      return { ...scene, levels: scene.levels.map((value, i) => i === index ? next : value) };
    }, createdItemIds);
  }

  private lastLabel = '';
  private pushPast(entry: HistoryEntry): void { this.past.push(entry); if (this.past.length > 60) this.past.shift(); }
  private requestSave(): void {
    if (this.isolated || this.sharedPending) return;
    if (this.savePending) return;
    this.savePending = true;
    queueMicrotask(() => { if (this.savePending) this.saveLocal(); });
  }

  /** False when the floor `pressed` (the one the gesture took at its press) has changed since: its pull would put the change back, so it ends like an Escape. */
  beginGeometryStroke(pressed?: Level): boolean {
    if (this.state.edit === 'none') return true;
    if (this.shared && (!this.shared.writable || this.sharedPending)) { void this.refuseShared(); return true; }
    this.clearPropertiesPreview();
    if (pressed && pressed !== this.level && shareEqual(pressed, this.level) !== pressed) return false;
    if (this.strokeBase || !this.state.scene) return true;
    if (this.savePending) this.saveLocal();
    this.strokeBase = { scene: this.state.scene, levelIndex: this.state.activeLevelIndex };
    const scope = this.captureDocumentScope();
    if (scope) this.gesture = { id: crypto.randomUUID(), before: { scene: this.state.scene, name: this.state.projectName }, scope, layoutVersion: this.sharedSnapshot?.layoutVersion ?? 0 };
    this.lastLabel = '';
    this.set({ isDragging: true, selectionAnchor: null });
    return true;
  }

  endStroke(): void {
    const base = this.strokeBase;
    // A door the gesture moved or resized takes, as it rests between two rooms, its copy in the room beyond (EnsureCrossWallMirror).
    const before = base?.levelIndex === this.state.activeLevelIndex ? base.scene.levels[base.levelIndex] : undefined;
    if (before) this.commitLevel('', level => mirrorMovedOpenings(before, level));
    const gesture = this.gesture;
    const scene = this.state.scene;
    this.strokeBase = null; this.lastLabel = '';
    if (this.shared) {
      this.gesture = null;
      if (base && scene && gesture && base.scene !== scene) {
        const scope = this.captureDocumentScope();
        if (scope && gesture.layoutVersion === this.sharedSnapshot?.layoutVersion && scope.projectId === gesture.scope.projectId && scope.epoch === gesture.scope.epoch && scope.levelIndex === gesture.scope.levelIndex
          && scope.scopeVersion === gesture.scope.scopeVersion) {
          const old = new Set(base.scene.levels[base.levelIndex]?.items.map(item => item.id));
          void this.scheduleShared(gesture.before, { scene, name: this.state.projectName }, gesture.scope, this.documentOwner,
            scene.levels[base.levelIndex].items.filter(item => !old.has(item.id)).map(item => item.id), '', true, 'edit', gesture.id);
        } else void this.refuseShared('scope_conflict');
      }
      this.publish({ isDragging: false });
      this.emitPreview();
      return;
    }
    if (base && base.scene !== this.state.scene) {
      const current = this.state.scene, committed = current && !this.isolated ? assignUniqueIds(base.scene, current) : current;
      this.pushPast(base); this.future = []; this.requestSave();
      const before = new Set(base.scene.levels[base.levelIndex]?.items.map(item => item.id));
      this.publish({ isDragging: false, ...(committed !== current ? { scene: committed } : {}) }, this.level?.items.filter(item => !before.has(item.id)).map(item => item.id) ?? [], true);
    }
    this.set({ isDragging: false });
  }

  cancelStroke(): void {
    const base = this.strokeBase;
    this.strokeBase = null; this.gesture = null; this.lastLabel = '';
    this.set({ ...(base ? { scene: this.sharedSnapshot?.scene ?? base.scene, activeLevelIndex: base.levelIndex } : {}), isDragging: false });
  }

  setActiveLevel(index: number): void {
    if (index !== this.state.activeLevelIndex && this.state.isolatedRoom !== null) this.set({ isolatedRoom: null });
    if (!Number.isInteger(index) || !this.state.scene?.levels[index] || index === this.state.activeLevelIndex) return;
    this.cancelStroke(); this.endMaterialTransform(false); this.lastLabel = '';
    this.set(this.floorInView(index, this.state.scene));
  }

  /**
   * What changes with the floor in view: the tool, the selection and the panels of the floor left behind go. 3D stays
   * in 3D, as the app's does: an empty floor is shown over the floors below it (`buildingShowsIn3D`). Only a floor with
   * nothing drawn on it or under it, which 3D would show as an empty stage, is shown on the plan.
   */
  private floorInView(index: number, scene: Scene): Partial<EditorState> {
    return { activeLevelIndex: index, tool: 'navigate', selection: null, selectionAnchor: null, resizeMode: false,
      selectionMaterialSlot: null, selectionMaterialSlots: [], materialTarget: null, showProperties: false, isCameraDragging: false,
      lastFloorPoint: null, isolatedRoom: null,
      ...(this.state.mode === 'scene' && !buildingShowsIn3D(scene.levels, index) ? { mode: 'plan' as const, wallMode: 'full' as const } : {}) };
  }

  /**
   * The floor menu's «Добавить этаж» / «Добавить подвал», as the app adds a level (AddLevel.CreateLevel): a copy
   * of the top floor over it, or of the lowest one under it, with its rooms, walls, doors and windows and what hangs
   * from its ceilings, cleared of furniture (`staysOnNewFloor`). The new floor comes into view. Levels run bottom to
   * top in the array.
   */
  addLevel(where: 'above' | 'basement'): void {
    if (!this.state.scene) return;
    this.cancelStroke(); this.endMaterialTransform(false);
    const scene = this.state.scene;
    if (!scene?.levels.length || !freeFloorSlots(scene.levels)) return;
    const { scene: next, index } = withLevel(scene, where);
    this.commit('', () => next, [], this.floorInView(index, next));
  }

  /**
   * A copy of a level right above it — walls, openings and furniture with ids of their own — and the copy becomes active.
   * Like a floor the app adds as a copy (AddLevel.CreateLevel), it is named by its place.
   */
  duplicateLevel(index: number): void {
    if (!this.state.scene?.levels[index] || !freeFloorSlots(this.state.scene.levels)) return;
    this.cancelStroke(); this.endMaterialTransform(false);
    const scene = this.state.scene, level = scene?.levels[index];
    if (!scene || !level) return;
    const levels = reindexed([...scene.levels.slice(0, index + 1), withoutRoofRooms(copyFloor(level, '')), ...scene.levels.slice(index + 1)]);
    const next = { ...scene, levels };
    this.commit('', () => next, [], this.floorInView(index + 1, next));
  }

  /**
   * The floor menu's «Удалить», once confirmed, as the app removes a level (RemoveLevel): the floor goes with
   * everything on it, in one undo step. The floor in view stays in view; when it is the one removed, the floor that
   * takes its place comes into view, or the new top one. A building keeps its last floor.
   */
  removeLevel(index: number): void {
    if (!Number.isInteger(index) || !this.state.scene?.levels[index] || this.state.scene.levels.length < 2) return;
    this.cancelStroke(); this.endMaterialTransform(false);
    const scene = this.state.scene, active = this.state.activeLevelIndex;
    if (!scene) return;
    const next = { ...scene, levels: reindexed(scene.levels.filter((_, i) => i !== index)) };
    this.commit('', () => next, [], active === index ? this.floorInView(Math.min(index, next.levels.length - 1), next)
      : { activeLevelIndex: active > index ? active - 1 : active });
  }

  renameLevel(index: number, name: string): void {
    const scene = this.state.scene;
    if (!scene?.levels[index] || scene.levels[index].name === name) return;
    this.commit('', current => ({ ...current, levels: current.levels.map((level, i) => i === index ? { ...level, name } : level) }));
  }

  /** The floor menu's drag handle: the level at `from` lands at `to`; the active level stays the same level. */
  moveLevel(from: number, to: number): void {
    const scene = this.state.scene;
    if (!scene || from === to || !scene.levels[from] || to < 0 || to >= scene.levels.length) return;
    this.cancelStroke(); this.endMaterialTransform(false);
    const levels = [...scene.levels];
    const [moved] = levels.splice(from, 1);
    levels.splice(to, 0, moved);
    const active = this.state.activeLevelIndex;
    const next = active === from ? to : from < active && to >= active ? active - 1 : from > active && to <= active ? active + 1 : active;
    this.commit('', current => ({ ...current, levels: reindexed(levels) }));
    if (next !== this.state.activeLevelIndex) this.set({ activeLevelIndex: next });
  }

  closeScene(): boolean {
    this.cancelStroke(); this.endMaterialTransform(false); this.saveLocal();
    if (this.state.scene && this.state.saveError && !this.shared) return false;
    this.past = []; this.future = []; this.strokeBase = null; this.lastLabel = '';
    this.epoch++;
    this.loadedProjectId = null;
    setCatalogProject(null);
    this.set({ ...INITIAL, loading: false });
    return true;
  }

  private applyGeometry(operation: (level: Level) => EditResult, preview = false): string | null {
    const source = preview ? this.strokeBase?.scene : this.state.scene;
    const index = this.state.activeLevelIndex;
    if (!source?.levels[index]) return t('#StoreNoProject');
    const result = operation(source.levels[index]);
    if (!result.level) return result.error;
    if (JSON.stringify(source.levels[index]) === JSON.stringify(result.level)) {
      if (preview) this.set({ scene: source });
      return null;
    }
    if (preview) {
      this.set({ scene: { ...source, levels: source.levels.map((level, i) => i === index ? result.level! : level) } });
    } else {
      this.commitLevel('', () => result.level!);
    }
    return null;
  }

  previewMoveVertex(wallId: string, end: 'start' | 'end', target: Vec2, connection?:VertexSnapTarget|null): string | null {
    return this.applyGeometry(level => geometry.moveVertex(level, wallId, end, target, connection), true);
  }

  previewMoveWall(id: string, delta: Vec2, connections?:EndpointConnection[], segment?:Pick<Wall,'start'|'end'>): string | null {
    return this.applyGeometry(level => geometry.moveWall(level, id, delta, connections, segment), true);
  }

  moveVertex(wallId: string, end: 'start' | 'end', target: Vec2, connection?:VertexSnapTarget|null): string | null {
    return this.applyGeometry(level => geometry.moveVertex(level, wallId, end, target, connection));
  }

  moveWall(id: string, delta: Vec2, connections?:EndpointConnection[]): string | null {
    return this.applyGeometry(level => geometry.moveWall(level, id, delta, connections));
  }

  /** A drawn wall at the thickness chosen for drawing; a catalog's wall brings its own thickness, height and finish. */
  addWall(start: Vec2, end: Vec2, own: { thickness?: number; height?: number; surface?: Surface } = {}): string | null {
    return this.applyGeometry(level => geometry.addWall(level, start, end, own.thickness ?? this.state.wallThickness, { height: own.height, surface: own.surface }));
  }

  /** The thickness the next drawn walls get; remembered across projects in this browser. */
  setDrawWallThickness(value: number): void {
    const thickness = clampWallThickness(value);
    this.set({ wallThickness: thickness });
    try { localStorage.setItem(WALL_THICKNESS_KEY, String(thickness)); } catch { /* private mode or quota: the choice lives for the session */ }
  }

  applyWallProperties(id: string, values: WallProperties): string | null {
    return this.applyProperties({ kind: 'wall', id, values });
  }

  applyOpeningProperties(id: string, values: OpeningProperties): string | null {
    return this.applyProperties({ kind: 'opening', id, values });
  }

  /** The UniqueIds of the pieces an undo or redo step still in a shared project's history adds or removes. */
  get historyPieces(): ReadonlySet<string> {
    const ids = new Set<string>();
    for (const { shared } of [...this.past, ...this.future]) {
      if (shared?.kind !== 'units') continue;
      for (const { before, after } of shared.floors.flatMap(floor => floor.items)) {
        if (!before && after?.uniqueId) ids.add(after.uniqueId);
        if (before?.uniqueId && !after) ids.add(before.uniqueId);
      }
    }
    return ids;
  }

  get canUndo(): boolean { return this.past.length > 0 && (!this.shared || this.shared.writable && !this.sharedPending); }
  get canRedo(): boolean { return this.future.length > 0 && (!this.shared || this.shared.writable && !this.sharedPending); }

  undo(count = 1): void {
    if (this.shared) { this.sharedHistory('undo', count); return; }
    this.clearPropertiesPreview();
    if (this.strokeBase) { this.cancelStroke(); return; }
    const prev = this.past.pop();
    if (!prev || !this.state.scene) return;
    this.future.push({ scene: this.state.scene, levelIndex: prev.levelIndex });
    this.lastLabel = '';
    this.publish({ scene: prev.scene, activeLevelIndex: prev.levelIndex, selection: null, selectionAnchor: null, resizeMode: false, materialTarget: null, materialTransformPreview: null }, [], true);
    this.saveLocal();
  }

  redo(count = 1): void {
    if (this.shared) { this.sharedHistory('redo', count); return; }
    this.clearPropertiesPreview();
    const next = this.future.pop();
    if (!next || !this.state.scene) return;
    this.pushPast({ scene: this.state.scene, levelIndex: next.levelIndex });
    this.lastLabel = '';
    this.publish({ scene: next.scene, activeLevelIndex: Math.min(next.levelIndex, Math.max(0, next.scene.levels.length - 1)), selection: null, selectionAnchor: null, resizeMode: false, materialTarget: null, materialTransformPreview: null }, [], true);
    this.saveLocal();
  }

  /** `levelIndex` re-opens the floor the project was left on; out-of-range falls to the first. */
  loadScene(scene: Scene, projectId: string, name: string, levelIndex = 0, options: { edit?: EditScope } = {}): void {
    if (this.savePending) this.saveLocal();
    this.strokeBase = null;
    this.shared = null; this.sharedSnapshot = null; this.sharedPending = null; this.remotePreviews = []; this.gesture = null;
    this.past = [];
    this.future = [];
    this.lastLabel = '';
    this.epoch++;
    this.loadedProjectId = projectId;
    scene = withBasementsSettled(scene);
    // A copy kept in this browser remembers the catalog project it came from in its scene.
    setCatalogProject(catalogProjectId(scene.projectId, projectId));
    this.set({
      scene,
      wallThickness: storedWallThickness() ?? defaultWallThickness(scene.culture),
      activeLevelIndex: Number.isInteger(levelIndex) ? Math.max(0, Math.min(levelIndex, scene.levels.length - 1)) : 0,
      isDragging: false,
      materialTarget: null, materialTransformPreview: null, resizeMode: false, planScale: null, planRotation: false, roomAction: null, selectionAnchor: null,
      lastFloorPoint: null, walkthrough: false, notice: null,
      // A guest has no walk tabs: they open in the orbit whatever camera the project was saved with.
      ...(options.edit === 'none' ? { firstPerson: false } : {}),
      projectId,
      projectName: name,
      loading: false,
      loadingStatus: null,
      error: null,
      selection: null,
      localSavedAt: (options.edit ?? 'all') !== 'all' ? null : this.readSavedAt(projectId),
      saveError: null,
      edit: options.edit ?? 'all',
    });
  }

  /** Persist the editable scene in the browser, so a refresh does not discard work. */
  saveLocal(): void {
    if (this.isolated || this.sharedPending || this.state.edit === 'none' || this.shared?.localCopy === false) return;
    this.savePending = false;
    const { projectId, projectName } = this.state;
    const scene = this.sharedSnapshot?.scene ?? this.strokeBase?.scene ?? this.state.scene;
    if (!scene || !projectId) return;
    const savedAt = Date.now();
    try {
      if (typeof localStorage === 'undefined') throw new Error('Storage unavailable');
      localStorage.setItem(snapshotKey(projectId), JSON.stringify({ scene, name: projectName, savedAt }));
      this.set({ localSavedAt: savedAt, saveError: null });
    } catch {
      this.set({ localSavedAt: null, saveError: t('#StoreLocalSaveFailed') });
    }
  }

  /** Read an autosave before fetching the remote project. The caller decides whether to use it. */
  restoreLocal(projectId: string): { scene: Scene; name: string; savedAt: number } | null {
    if (projectId === this.state.projectId && this.savePending) this.saveLocal();
    if (!projectId) return null;
    try {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(snapshotKey(projectId));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { scene?: Scene; name?: string; savedAt?: number };
      if (!parsed.scene || typeof parsed.savedAt !== 'number') return null;
      return { scene: repairLegacyPivots(parsed.scene, projectId), name: parsed.name || t('#AppProjectFallbackName'), savedAt: parsed.savedAt };
    } catch {
      return null;
    }
  }

  clearLocal(): void {
    if (!this.state.projectId || typeof localStorage === 'undefined') return;
    try { localStorage.removeItem(snapshotKey(this.state.projectId)); } catch { /* ignore */ }
    this.set({ localSavedAt: null });
  }

  private readSavedAt(projectId: string): number | null {
    if (!projectId) return null;
    try {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(snapshotKey(projectId));
      const parsed = raw ? JSON.parse(raw) as { savedAt?: number } : null;
      return parsed && typeof parsed.savedAt === 'number' ? parsed.savedAt : null;
    } catch {
      return null;
    }
  }

  select(selection: Selection, anchor: { x: number; y: number } | null = null, slot?: string | null): void {
    if (selection && this.state.edit === 'none') return;
    this.set({ selection, selectionAnchor: anchor, showVariations: false });
    if (slot !== undefined) this.set({ selectionMaterialSlot: slot });
  }

  /**
   * The demo walkthrough on (the app's EnableDemoMode: the walk view, `EditorRoomMode.Demo`) or off again. It needs a
   * floor that shows in 3D; going in lets the selection go and puts the panels away.
   */
  setWalkthrough(on: boolean): void {
    if (!on) { if (this.state.walkthrough) this.set({ walkthrough: false }); return; }
    if (!showsIn3D(this.level)) return;
    this.cancelStroke(); this.endMaterialTransform(false);
    this.set({ walkthrough: true, mode: 'scene', firstPerson: true, wallMode: 'full', showCeiling: true, selection: null, selectionAnchor: null,
      tool: 'navigate', resizeMode: false, showCatalog: false, showProperties: false, showVariations: false, showLighting: false, showMinimap: false,
      showGallery: false, cameraMode: false, materialTarget: null, showWindowViews: false });
  }

  /**
   * The resize mode of the selected piece, or of the selected door or window in 3D and on the wall plan, where the app
   * shows an opening's resize markers (on the floor plan an opening has its width handles without it).
   */
  setResizeMode(on: boolean): void {
    const { selection, mode, planSurface } = this.state;
    const opening = selection?.kind === 'opening' && (mode === 'scene' || planSurface === 'wall') && !!this.level?.openings.some(value => value.id === selection.id);
    this.set({ resizeMode: on && (!!this.selectedItem() || opening), selectionAnchor: null });
  }

  /**
   * «Изменить масштаб плана» (the app's `BlueprintScale`): the floor plan, where every wall's length and every room's
   * area wait for a real value to be typed. Every floor is scaled when there are several, unless told otherwise.
   */
  /** «Измерить линейкой» (the app's measuring mode, MeasuringModeState): the floor plan with the ruler armed. */
  startMeasuring(): void {
    if (!this.level) return;
    this.select(null);
    this.set({ mode: 'plan', planSurface: 'floor', wallView: null, resizeMode: false, showCatalog: false, showProperties: false, materialTarget: null, planScale: null });
    this.set({ tool: 'measure', measurements: [] });
  }
  addMeasurement(from: Vec2, to: Vec2): void {
    if (this.state.tool === 'measure') this.set({ measurements: [...this.state.measurements, { from: { ...from }, to: { ...to } }] });
  }
  clearMeasurements(): void { if (this.state.measurements.length) this.set({ measurements: [] }); }

  startPlanScale(): void {
    if (!this.level) return;
    this.select(null);
    this.set({ mode: 'plan', planSurface: 'floor', wallView: null, tool: 'navigate', resizeMode: false, showCatalog: false,
      showProperties: false, materialTarget: null, planScale: { everyFloor: (this.state.scene?.levels.length ?? 1) > 1 } });
  }
  endPlanScale(): void { if (this.state.planScale) this.set({ planScale: null }); }
  /** «Повернуть план»: the floor plan in view, nothing selected, the handle out (the app's plan rotation). */
  startPlanRotation(): void {
    if (!this.level?.walls.length) return;
    this.select(null);
    this.set({ mode: 'plan', planSurface: 'floor', wallView: null, tool: 'navigate', resizeMode: false, showCatalog: false, showProperties: false,
      materialTarget: null, planScale: null, planRotation: true });
  }
  endPlanRotation(): void { if (this.state.planRotation) { this.cancelStroke(); this.set({ planRotation: false }); } }
  setPlanScaleFloors(everyFloor: boolean): void { if (this.state.planScale) this.set({ planScale: { everyFloor } }); }

  /** A real length or area typed over the one shown sets the plan's scale as one step, and the mode ends as the app's does. */
  scalePlanTo(kind: 'length' | 'area', typed: number, shown: number): string | null {
    const mode = this.state.planScale, scene = this.state.scene;
    if (!mode || !scene) return null;
    const asked = scaleFactor(kind, typed, shown);
    if (asked && 'error' in asked) return asked.error;
    if (asked) {
      const index = this.state.activeLevelIndex;
      this.select(null);
      this.commit('Масштаб плана', current => scalePlan(current, index, asked.factor, mode.everyFloor)); // i18n-ignore: an undo-merge label, compared, never shown
    }
    this.set({ planScale: null });
    return null;
  }

  /** A wall's length typed on its label (`wallLength.ts`), as one step. */
  setWallLength(id: string, length: number): string | null {
    const level = this.level;
    if (!level) return t('#StorePlanNotFound');
    const result = typedWallLength(level, id, length);
    if (!result.level) return result.error;
    if (result.level !== level) this.commitLevel('Длина стены', () => result.level!); // i18n-ignore: an undo-merge label, compared, never shown
    return null;
  }

  /**
   * «Вид из окна» over the 3D view, where the choice is seen: from the plan it switches to 3D, and it closes the other
   * panels as the material sheet does.
   */
  openWindowViews(): void {
    this.set({ mode: 'scene', showWindowViews: true, showCatalog: false, materialTarget: null, showProperties: false,
      showVariations: false, showLighting: false, showMinimap: false, cameraMode: false });
  }

  /** Whether the wall plan has a wall to show: one with a room on a side of it. */
  get canOpenWallPlan(): boolean { return !!this.level && defaultWallView(this.level, this.selectedItem()) !== null; }

  /**
   * The floor / ceiling / wall switch under the plan. Entering or leaving the wall plan drops
   * the selection, as the app does when the plane changes; the wall plan opens on the app's
   * default wall (the selected item's room, its longest interior face, exterior walls first).
   */
  setPlanSurface(surface: PlanSurface): void {
    if (surface === this.state.planSurface) return;
    const wallView = surface === 'wall' && this.level ? defaultWallView(this.level, this.selectedItem()) : null;
    if (surface === 'wall' && !wallView) return;
    const crossing = surface === 'wall' || this.state.planSurface === 'wall';
    // The wall plan is a plane of the 2D plan: asked for from 3D (the agent can), it opens the plan first.
    const mode = surface === 'wall' && this.state.mode !== 'plan' ? { mode: 'plan' as const, wallMode: 'full' as const } : {};
    this.set({ ...mode, planSurface: surface, wallView, ...(crossing ? { selection: null, selectionAnchor: null } : {}) });
  }

  /** ‹ › in the wall plan: the next interior face, in wall order (the app's NextWall2D). The selection goes with the wall. */
  stepWallView(step: number): void {
    const level = this.level, current = this.state.wallView;
    if (!level || !current || this.state.planSurface !== 'wall') return;
    const next = stepWallView(level, current, step);
    if (next) this.set({ wallView: next, selection: null, selectionAnchor: null });
  }
  /** ‹ › in the ceiling plan: the next room's ceiling, selected, in room order (`ceilingStepper.ts`). */
  stepCeiling(step: number): void {
    const level = this.level, selection = this.state.selection;
    if (!level || this.state.mode !== 'plan' || this.state.planSurface !== 'ceiling') return;
    const next = stepCeiling(level, selection?.kind === 'room' ? selection.index : null, step);
    if (next != null) this.select({ kind: 'room', index: next });
  }
  setCameraDragging(on:boolean):void {
    this.set({isCameraDragging:on,...(on&&this.state.selection?.kind==='item'?{selectionAnchor:null}:{})});
  }

  beginMaterialTransform(target: NonNullable<MaterialTarget>, baseline: Surface): void {
    if (this.state.edit === 'none' || this.refusedInRoom(target.kind === 'item' ? LIVE_PAINT_IN_APP : ITEMS_ONLY)) return;
    if (this.shared && (!this.shared.writable || this.sharedPending || this.strokeBase || this.state.propertiesPreview)) { void this.refuseShared(); return; }
    const selection=this.state.selection;
    const slots=target.kind==='item'&&selection?.kind==='item'&&selection.id===target.itemId
      ||target.kind==='opening'&&selection?.kind==='opening'&&selection.id===target.openingId?this.state.selectionMaterialSlots:[];
    this.set({ materialTransformPreview: { target: structuredClone(target), baseline: structuredClone(baseline), draft: structuredClone(baseline), slots:structuredClone(slots) } });
  }
  previewMaterialTransform(draft: Surface): void {
    const preview = this.state.materialTransformPreview;
    if (preview) this.set({ materialTransformPreview: { ...preview, draft: structuredClone(draft) } });
  }
  endMaterialTransform(commit = true): void {
    const preview = this.state.materialTransformPreview;
    if (!preview) return;
    const stale = this.shared && this.gesture && this.gesture.layoutVersion !== this.sharedSnapshot?.layoutVersion;
    this.endingDraft = true;
    this.set({ materialTransformPreview: null });
    if (!commit || stale || JSON.stringify(preview.baseline) === JSON.stringify(preview.draft)) {
      this.endingDraft = false;
      if (this.sharedSnapshot && !this.sharedPending) this.publish({ scene: this.sharedSnapshot.scene, projectName: this.sharedSnapshot.name });
      if (commit && stale) void this.refuseShared('scope_conflict');
      return;
    }
    if(preview.target.kind==='item'){
      const target=preview.target;
      this.commitLevel('', level => updateItem(level, target.itemId, item => {
        const next = structuredClone(item);
        transformItemMaterials(next, target.slot, preview.baseline, preview.draft, preview.slots ?? []);
        return JSON.stringify(next) === JSON.stringify(item) ? item : next;
      }));
    }else if(preview.target.kind==='opening'){
      const target=preview.target;
      this.commitLevel('', level => updateOpeningMaterials(level, target.openingId, opening => transformItemMaterials(opening, target.slot, preview.baseline, preview.draft, preview.slots ?? [])));
    }else this.setSurface(preview.target, preview.draft);
    this.endingDraft = false;
    this.emitPreview();
  }

  /**
   * Run several edits as one undo step — an agent tool call, a scripted command. Every commit
   * inside still publishes and autosaves; only the history collapses to the state before.
   */
  runBatch<T>(work: () => T): T {
    if (this.batchBase || this.strokeBase || !this.state.scene) return work();
    this.batchBase = { scene: this.state.scene, levelIndex: this.state.activeLevelIndex, depth: this.past.length };
    try { return work(); } finally { this.endBatch(); }
  }
  private batchBase: (HistoryEntry & { depth: number }) | null = null;
  private endBatch(): void {
    const base = this.batchBase;
    this.batchBase = null;
    if (!base || base.scene === this.state.scene) return;
    this.past.length = Math.min(this.past.length, base.depth);
    this.pushPast({ scene: base.scene, levelIndex: base.levelIndex });
    this.future = []; this.lastLabel = '';
  }

  /** How many undo steps exist — a caller that groups its own work compares before and after. */
  get historyDepth(): number { return this.past.length; }
  get redoDepth(): number { return this.future.length; }

  /** Put a whole document back, as one undoable step (an agent answer being rolled back). */
  restoreScene(scene: Scene): void {
    this.cancelStroke(); this.endMaterialTransform(false); this.lastLabel = '';
    this.replacing = true;
    try { this.commit('', () => scene); } finally { this.replacing = false; }
    this.select(null);
  }
  private replacing = false;

  setRoomProperties(index: number, values: { name?: string; roomType?: string; height?: number }): string | null {
    const room = this.level?.rooms.find(value => value.index === index);
    if (!room) return t('#RoomSizeRoomNotFound');
    this.commitLevel('', level => ({ ...level, rooms: level.rooms.map(value => value.index !== index ? value
      : { ...value, ...(values.name !== undefined ? { name: values.name } : {}), ...(values.roomType !== undefined ? { roomType: values.roomType } : {}),
        ...(values.height !== undefined ? { height: values.height } : {}) }) }));
    return null;
  }

  removeItems(ids: readonly string[]): number {
    if (this.refuseLocked(ids)) return 0;
    const remove = new Set(ids);
    const before = this.level?.items.length ?? 0;
    this.commitLevel('', level => remove.size && level.items.some(item => remove.has(item.id)) ? { ...level, items: level.items.filter(item => !remove.has(item.id)) } : level);
    const selected = this.state.selection;
    if (selected?.kind === 'item' && remove.has(selected.id) && !this.level?.items.some(item => item.id === selected.id)) this.select(null);
    return before - (this.level?.items.length ?? 0);
  }

  /** Removes the openings `ids` with their copies on the other rooms' walls; the count is of `ids` alone. */
  removeOpenings(ids: readonly string[]): number {
    const remove = new Set(ids), present = this.level?.openings.filter(opening => remove.has(opening.id)).map(opening => opening.id) ?? [];
    this.commitLevel('', level => remove.size && level.openings.some(opening => remove.has(opening.id))
      ? syncOpeningTwins(level, { ...level, openings: level.openings.filter(opening => !remove.has(opening.id)) }, remove) : level);
    const selection = this.state.selection;
    if (selection?.kind === 'opening' && !this.level?.openings.some(opening => opening.id === selection.id)) this.select(null);
    return present.filter(id => !this.level?.openings.some(opening => opening.id === id)).length;
  }

  removeWall(id: string): string | null {
    const error = this.applyGeometry(level => geometry.removeWall(level, id));
    if (!error && this.state.selection && 'id' in this.state.selection && this.state.selection.id === id) this.select(null);
    if (!error && this.state.selection?.kind === 'vertex' && this.state.selection.wallId === id) this.select(null);
    return error;
  }

  // ---- lookups -------------------------------------------------------------
  get presentedScene(): Scene | null {
    const preview = this.state.propertiesPreview;
    let scene = preview && preview.baseScene === this.state.scene && preview.projectId === this.state.projectId
      && preview.levelIndex === this.state.activeLevelIndex && preview.target === propertyTarget(this.state.selection)
      ? preview.scene : this.state.scene;
    if (!scene || !this.remotePreviews.length) return scene;
    const base = scene, cached = this.presentationCache, layout = this.sharedSnapshot?.layoutVersion;
    if (cached && cached.base === base && cached.previews === this.remotePreviews && cached.gesture === this.gesture && cached.layout === layout) return cached.scene;
    // Protect only this browser's edits, once; an earlier remote preview is not a local edit.
    const validated = new Set<Level>();
    const localSlot = this.gesture?.scope.levelIndex;
    const localTouched = this.gesture && localSlot !== undefined
      ? previewDelta(this.gesture.before.scene.levels[localSlot], base.levels[localSlot]) : null;
    for (const remote of this.remotePreviews) {
      if (!remote.preview || remote.floor.layoutVersion !== this.sharedSnapshot?.layoutVersion
        || remote.preview.baseRevision < (this.sharedSnapshot?.revision ?? 0)) continue;
      const slot = remote.floor.slot, level = scene.levels[slot];
      if (!level) continue;
      let change = remote.preview.delta;
      if (this.gesture && slot === this.gesture.scope.levelIndex) {
        const touched = localTouched;
        if (touched?.ok) {
          const protect = <T, K>(delta: { upsert: T[]; remove: K[] }, local: { upsert: T[]; remove: K[] }, key: (value: T) => K) => {
            const keys = new Set([...local.upsert.map(key), ...local.remove]);
            return { upsert: delta.upsert.filter(value => !keys.has(key(value))), remove: delta.remove.filter(value => !keys.has(value)) };
          };
          change = { items: protect(change.items, touched.value.items, v => v.id), walls: protect(change.walls, touched.value.walls, v => v.id),
            openings: protect(change.openings, touched.value.openings, v => v.id), rooms: protect(change.rooms, touched.value.rooms, v => v.index),
            ...(!touched.value.outline && change.outline ? { outline: change.outline } : {}) };
        }
      }
      const applied = applyPreviewDelta(level, change, validated);
      if (applied.ok) scene = { ...scene, levels: scene.levels.map((value, index) => index === slot ? applied.value : value) };
    }
    if (cached) scene = shareEqual(cached.scene, scene);
    this.presentationCache = { base, previews: this.remotePreviews, gesture: this.gesture, layout, scene };
    return scene;
  }
  get presentedLevel(): Level | undefined { return this.presentedScene?.levels[this.state.activeLevelIndex]; }

  previewProperties(edit: PropertyEdit, owner?: object): string | null {
    if (this.state.edit === 'none') return null;
    if (this.shared && (!this.shared.writable || this.sharedPending)) { void this.refuseShared(); return null; }
    const scene = this.state.scene, level = this.level;
    if (!scene || !level || this.state.isDragging || propertyTarget(this.state.selection) !== editTarget(edit)) return null;
    const result = editProperties(level, edit);
    if (!result.level || result.level === level) { this.clearPropertiesPreview(owner); return result.error ?? null; }
    this.set({ propertiesPreview: { owner, target: editTarget(edit), projectId: this.state.projectId,
      levelIndex: this.state.activeLevelIndex, baseScene: scene,
      scene: { ...scene, levels: scene.levels.map((value, index) => index === this.state.activeLevelIndex ? result.level! : value) } } });
    return null;
  }

  /** Owner checks keep an exiting inspector from clearing a newer target's draft. */
  clearPropertiesPreview(owner?: object): void {
    if (this.state.propertiesPreview && (!owner || this.state.propertiesPreview.owner === owner)) this.set({ propertiesPreview: null });
  }

  applyProperties(edit: PropertyEdit): string | null {
    const level = this.level;
    if (!level) return t('#StorePlanNotFound');
    // In a gesture an opening's copies are paired as it found them, so a copy that had to go midway comes back.
    const pairs = this.strokeBase && this.strokeBase.levelIndex === this.state.activeLevelIndex ? this.strokeBase.scene.levels[this.strokeBase.levelIndex] : undefined;
    const result = editProperties(level, edit, pairs ?? level);
    if (!result.level || result.level === level) { this.clearPropertiesPreview(); return result.error ?? null; }
    if (edit.kind === 'item' && !edit.replacement && placeChanged(level, result.level, edit.id)) { const error = this.refuseLocked([edit.id]); if (error) return error; }
    // A door moved between two rooms gets its copy in the other room at once, and in a gesture as it ends (endStroke).
    const next = this.strokeBase || edit.kind !== 'opening' ? result.level : mirrorMovedOpenings(level, result.level);
    return this.commitLevel('', () => next);
  }

  /**
   * «Для всех (N)» of a replacement (the app's ReplaceAllSameTypeFurniture): the piece takes its edit, and each piece
   * named in `others` the new model at its catalog size, keeping its place, height and turn — one undo step. How many
   * pieces now are the new model, or why none is.
   */
  replaceSameKind(edit: Extract<PropertyEdit, { kind: 'item' }>, others: readonly string[]): { count: number; error?: undefined } | { error: string } {
    const level = this.level, replacement = edit.replacement;
    if (!level || !replacement) return { error: t('#GeometryItemNotFound') };
    const result = editProperties(level, edit);
    if (!result.level) return { error: result.error };
    const size = { w: replacement.size[0], h: replacement.size[1], d: replacement.size[2] }, ids = new Set(others);
    let count = 1;
    try {
      const items = result.level.items.map(item => {
        if (!ids.has(item.id)) return item;
        const updated = structuredClone(item);
        applyItemProperties(updated, { size, position: item.position, elevation: item.elevation, rotation: item.rotation }, replacement);
        Object.assign(updated, replacementPlace(result.level!, item, replacement, updated));
        count++;
        return updated;
      });
      const refused = this.commitLevel('', () => ({ ...result.level!, items }));
      if (refused) return { error: refused };
    } catch (failure) {
      return { error: failure instanceof Error ? failure.message : t('#StoreReplaceItemsFailed') };
    }
    return { count };
  }

  get level() { return this.state.scene?.levels[this.state.activeLevelIndex]; }

  selectedWall(): Wall | undefined {
    const s = this.state.selection;
    return s?.kind === 'wall' ? this.level?.walls.find((w) => w.id === s.id) : undefined;
  }

  selectedItem(): Item | undefined {
    const s = this.state.selection;
    return s?.kind === 'item' ? this.level?.items.find((i) => i.id === s.id) : undefined;
  }

  selectedOpening(): Opening | undefined {
    const s = this.state.selection;
    return s?.kind === 'opening' ? this.level?.openings.find((o) => o.id === s.id) : undefined;
  }

  /** The selected plinth or ceiling molding with its wall, while the wall still has it. */
  selectedTrim(): { wall: Wall; trim: Trim; value: NonNullable<Wall['plinth']> } | undefined {
    const s = this.state.selection;
    if (s?.kind !== 'trim') return undefined;
    const wall = this.level?.walls.find(w => w.id === s.wallId), value = wall?.[s.trim];
    return wall && value ? { wall, trim: s.trim, value } : undefined;
  }

  // ---- edits ---------------------------------------------------------------
  /** The mounted renderer's reading of the loaded meshes, so an edit made without a pointer — the
   * context bar's turn, an agent's move — carries a passenger on a seat as a drag would. */
  surfaceProbe: SurfaceProbe | null = null;

  /** Moving and turning an item carries whatever stands on it, exactly as dragging does. */
  moveItem(id: string, to: Vec2): string | null {
    const position = { x: round(to.x), z: round(to.z) };
    return this.refuseLocked([id]) ?? this.commitLevel(`move:${id}`, level => {
      const item = level.items.find(value => value.id === id);
      return item ? transformItemWithStack(level, id, { position, rotation: item.rotation }, undefined, this.surfaceProbe ?? undefined) : level;
    });
  }

  rotateItem(id: string, radians: number): string | null {
    return this.refuseLocked([id]) ?? this.commitLevel(`rotate:${id}`, level => {
      const item = level.items.find(value => value.id === id);
      return item ? transformItemWithStack(level, id, { position: item.position, rotation: radians }, undefined, this.surfaceProbe ?? undefined) : level;
    });
  }

  resizeItem(id: string, size: Partial<Item['size']>): string | null {
    return this.refuseLocked([id]) ?? this.commitLevel(`resize:${id}`, level => updateItem(level, id, item => {
      const dimensions = { ...item.size, ...size };
      if (dimensions.w === item.size.w && dimensions.h === item.size.h && dimensions.d === item.size.d) return item;
      const next = { ...item };
      applyItemProperties(next, { size: dimensions, position: item.position, elevation: item.elevation, rotation: item.rotation });
      return next;
    }));
  }

  /**
   * One of the model's own colours (the app's FurnitureOptionPopup, which swaps the piece's variation): the piece wears
   * it by its `variationId`, and what was painted over its parts gives way to it.
   */
  setItemVariation(id: string, variationId: string): void {
    const painted = (item: Item) => this.state.edit === 'items' ? partPainted(item) : !!item.materials;
    this.commitLevel('', level => updateItem(level, id, item => item.variationId === variationId && !painted(item) ? item
      : { ...item, variationId, materials: undefined }));
  }

  setItemElevation(id: string, elevation: number): string | null {
    const next = Math.max(0, round(elevation));
    return this.refuseLocked([id]) ?? this.commitLevel(`elev:${id}`, level => updateItem(level, id, item => item.elevation === next ? item : { ...item, elevation: next }));
  }

  deleteSelection(): void {
    const selection = this.state.selection;
    if (!selection) return;
    if (selection.kind === 'item' || selection.kind === 'items') {
      // The app deletes a table with what stands on it: the set a move carries. A locked pick stays, and says so.
      const { ids, held } = movableItems(this.level ?? { items: [] }, selectedItemIds(selection), this.surfaceProbe ?? undefined);
      if (!ids.length) { this.set({ error: t('#WebItemLocked') }); return; }
      const gone = new Set(withRiders(this.level?.items ?? [], ids, this.surfaceProbe ?? undefined).map(({ item }) => item.id));
      const error = this.commitLevel('', level => level.items.some(item => gone.has(item.id)) ? { ...level, items: level.items.filter(item => !gone.has(item.id)) } : level);
      if (error) { this.set({ error }); return; }
      this.select(null);
      if (held) this.notifyLockedStayed(held);
      return;
    }
    if (this.refusedInRoom()) return;
    if (selection.kind === 'vertex') {
      // A corner that cannot go — three walls meet there, or the merged wall would break a rule — stays selected and says why.
      const error = this.applyGeometry(level => geometry.removeVertex(level, selection.wallId, selection.end));
      if (error) { this.set({ error }); return; }
      this.select(null); return;
    }
    if (selection.kind === 'wall') this.applyGeometry(level => geometry.removeWall(level, selection.id));
    else this.commitLevel('', level => {
      if (selection.kind === 'opening') return syncOpeningTwins(level, { ...level, openings: level.openings.filter(opening => opening.id !== selection.id) }, [selection.id]);
      // The app's RemovePlinth hides the plinth or the molding of that wall side.
      if (selection.kind === 'trim') return withWallTrim(level, selection.wallId, selection.trim, false);
      return level;
    });
    this.select(null);
  }

  duplicateSelection(): void {
    const selection = this.state.selection, level = this.level;
    if (selection?.kind === 'room') { this.askRoomAction('clone'); return; }
    if (!level || (selection?.kind !== 'item' && selection?.kind !== 'items')) return;
    const picked = selectedItemIds(selection);
    // Each copy shifts with the picked piece it goes with — along its wall for a wall piece, across the floor otherwise; the copies of the picked pieces become the selection.
    const sources = withRiders(level.items, picked, this.surfaceProbe ?? undefined);
    const copies = sources.map(({ item, base }) => { const shift = copyShift(level, base); return this.copied(item, { x: item.position.x + shift.x, z: item.position.z + shift.z }); });
    this.commitCopies(copies, copies.filter((_, index) => picked.includes(sources[index].item.id)));
  }

  /** Adds `copies` in one undo step and selects `chosen` of them once they landed; the commit's refusal, else null. */
  private commitCopies(copies: Item[], chosen: Item[]): { error: string | null; landed: boolean } {
    if (!chosen.length) return { error: null, landed: false };
    const error = this.commitLevel('', value => ({ ...value, items: [...value.items, ...copies] }), copies.map(copy => copy.id));
    if (error || !this.level?.items.some(item => item.id === chosen[0].id)) return { error, landed: false };
    this.select(chosen.length > 1 ? { kind: 'items', ids: chosen.map(copy => copy.id) } : { kind: 'item', id: chosen[0].id });
    return { error: null, landed: true };
  }

  /** «Очистить план»: the floor's walls, rooms, openings and furniture, locked pieces included, in one undo step. */
  clearPlan(): string | null {
    return this.commitLevel('', level => ({ ...level, walls: [], rooms: [], items: [], openings: [], outline: [] }));
  }

  /**
   * Why a direct edit of `ids` — its own gesture, key, field, Delete or agent tool — is refused: one of them is locked (the
   * app's `Furniture.Locked`). Edits that carry pieces as a side effect (a room, a wall, a floor) never ask.
   */
  lockedItemsRefusal(ids: readonly string[]): string | null {
    return this.level?.items.some(item => item.locked && ids.includes(item.id)) ? t('#WebItemLocked') : null;
  }

  /** «N закреплённых предметов остались на месте», once per gesture or command that left them. */
  notifyLockedStayed(count: number): void {
    if (count > 0) this.set({ notice: { text: plural('#WebLockedStayed', count), key: (this.state.notice?.key ?? 0) + 1, icon: 'padlock' } });
  }

  /** The refusal said where the user looks, and handed back. */
  private refuseLocked(ids: readonly string[]): string | null {
    const error = this.lockedItemsRefusal(ids);
    if (error) this.set({ error });
    return error;
  }

  /** ⇧⌘L: the selected furniture locked, or unlocked when all of it already is; one undo step. */
  toggleLock(): string | null {
    const ids = selectedItemIds(this.state.selection), items = this.level?.items.filter(item => ids.includes(item.id)) ?? [];
    if (!items.length) return null;
    const lock = !items.every(item => item.locked);
    return this.commitLevel('', level => ({ ...level, items: level.items.map(item => {
      if (!ids.includes(item.id)) return item;
      const { locked: _, ...rest } = item;
      return lock ? { ...rest, locked: true } : rest;
    }) }));
  }

  /** ⌘C: the selected furniture with what stands on it, kept for this project only, outside history and the saved file. */
  copySelection(): boolean {
    const selection = this.state.selection, level = this.level;
    if (!level || (selection?.kind !== 'item' && selection?.kind !== 'items')) return false;
    const picked = selectedItemIds(selection);
    const sources = withRiders(level.items, picked, this.surfaceProbe ?? undefined);
    if (!sources.some(({ item }) => picked.includes(item.id))) return false;
    this.clipboard = { projectId: this.state.projectId, picked, sources: sources.map(({ item, base }) => ({ item: structuredClone(item), baseId: base.id })) };
    return true;
  }

  canPaste(): boolean {
    return !!this.clipboard && this.clipboard.projectId === this.state.projectId && !!this.level;
  }

  /**
   * ⌘V and «Вставить сюда»: copies of the copied furniture in one undo step, the picked pieces' middle at `at`, else each
   * shifted from its source as a duplicate is; a wall piece stays on its wall and is left out on a floor without it.
   */
  pasteItems(at?: Vec2): string | null {
    const clip = this.clipboard, level = this.level;
    if (!clip || !level || !this.canPaste()) return null;
    const bases = new Map(clip.sources.filter(({ item }) => clip.picked.includes(item.id)).map(({ item }) => [item.id, item]));
    const kept = clip.sources.filter(({ baseId }) => { const base = bases.get(baseId); return !!base && (base.placement !== 'wall' || level.walls.some(wall => wall.id === base.wallId)); });
    const floor = [...bases.values()].filter(base => base.placement !== 'wall' && kept.some(({ item }) => item.id === base.id));
    const middle = floor.length ? { x: floor.reduce((sum, base) => sum + base.position.x, 0) / floor.length, z: floor.reduce((sum, base) => sum + base.position.z, 0) / floor.length } : null;
    const shifts = new Map([...bases.values()].map(base => [base.id, at && middle && base.placement !== 'wall' ? { x: at.x - middle.x, z: at.z - middle.z } : copyShift(level, base)]));
    // The live room sends a paste as a copy of its source, which rt takes only from this floor and unchanged.
    if (this.shared?.mintItem && kept.some(({ item }) => JSON.stringify(level.items.find(each => each.id === item.id)) !== JSON.stringify(item))) return t('#WebLivePasteNeedsSource');
    const copies = kept.map(({ item, baseId }) => { const shift = shifts.get(baseId)!; return this.copied(item, { x: item.position.x + shift.x, z: item.position.z + shift.z }); });
    const { error, landed } = this.commitCopies(copies, copies.filter((_, index) => clip.picked.includes(kept[index].item.id)));
    if (!landed) return error;
    const ids = new Map(kept.map(({ item }, index) => [item.id, copies[index].id]));
    const placed = (id: string) => structuredClone(this.level?.items.find(item => item.id === id) ?? copies.find(copy => copy.id === id)!);
    this.clipboard = { ...clip, picked: clip.picked.map(id => ids.get(id) ?? id),
      sources: clip.sources.map(source => ids.has(source.item.id) ? { item: placed(ids.get(source.item.id)!), baseId: ids.get(source.baseId)! } : source) };
    return null;
  }

  /** A new piece's identity: the shared project's own when its adapter mints one, else the web's id, its `UniqueId` given at the commit. */
  newItemIdentity(origin?: NewItemOrigin): ItemIdentity {
    return this.shared?.mintItem?.(origin) ?? { id: `i_${crypto.randomUUID()}` };
  }

  private copied(item: Item, position: Vec2): Item {
    const { id, uniqueId } = this.newItemIdentity({ copyOf: item.id });
    const copy: Item = { ...structuredClone(item), id, position };
    if (uniqueId) copy.uniqueId = uniqueId;
    else delete copy.uniqueId;
    return copy;
  }

  setWallThickness(id: string, thickness: number): string | null {
    return this.applyWallProperties(id, { thickness });
  }

  setWallHeight(id: string, height: number): string | null {
    return this.applyWallProperties(id, { height });
  }

  /**
   * A finish on its target, or with `all` on more: every room's floor or ceiling of the floor; for a wall, `'room'` the
   * faces of its room it reaches through shared ends (the material sheet's «Ко всем», ApplyMaterialToAllWalls) and
   * `true` that side of every wall (the agent's apply_to_all); for a plinth or a molding, every one of its kind in the room.
   */
  setSurface(target: NonNullable<MaterialTarget>, surface: Surface, all: boolean | 'room' = false): string | null {
    return this.commitLevel('', level => surfaceEdit(level, target, surface, all));
  }

  /**
   * A finish shown on its target without a commit, an undo step or a save — the custom colour's live look while it is
   * being chosen, as the app's CustomColorPopup shows it (a properties preview of the selection; `owner` takes it away).
   */
  previewSurface(target: NonNullable<MaterialTarget>, surface: Surface, owner?: object): void {
    const scene = this.state.scene, level = this.level, key = propertyTarget(this.state.selection);
    if (!scene || !level || !key || this.state.isDragging) return;
    const next = surfaceEdit(level, target, surface, false);
    if (next === level) { this.clearPropertiesPreview(owner); return; }
    this.set({ propertiesPreview: { owner, target: key, projectId: this.state.projectId, levelIndex: this.state.activeLevelIndex, baseScene: scene,
      scene: { ...scene, levels: scene.levels.map((value, index) => index === this.state.activeLevelIndex ? next : value) } } });
  }

  /** A new opening on the wall: the editor's own of `kind`, or with `model` a catalog door, window or arch (`insertOpening`). */
  addOpening(wallId: string, kind: OpeningKind, offset?: number, model?: CatalogOpening): string | null {
    return this.applyGeometry(level => insertOpening(level, wallId, kind, offset, model));
  }

  addRectangularRoom(start: Vec2, end: Vec2): string | null {
    return this.applyGeometry(level => rectangularRoom(level, start, end, this.state.wallThickness));
  }

  moveOpening(id: string, offset: number): string | null {
    const opening = this.level?.openings.find(o => o.id === id);
    const wall = this.level?.walls.find(w => w.id === opening?.wallId);
    if (!opening || !wall) return t('#GeometryOpeningNotFound');
    return this.applyOpeningProperties(id, { offset: clamp(offset, opening.width / 2 + 0.02, wall.length - opening.width / 2 - 0.02) });
  }

  // ---- the object toolbar's commands (src/ui/contextToolbar.ts) ------------
  /**
   * «Удалить» of the object toolbar and the Delete key: the app's RemoveItem, which its Delete key runs too
   * (`InputController.OnDelete`). On the floor plan a room's own wall is not removed at once — the app asks
   * whether to remove it or only hide it. A wall picked by its face, in 3D or on the wall plan, goes only when it
   * stands alone. A room is removed on the floor plan only, after «Удалить комнату?» (RemoveRoom).
   */
  removeSelection(): void {
    const selection = this.state.selection, level = this.level;
    if (!selection || !level) return;
    if (selection.kind !== 'item' && selection.kind !== 'items' && this.refusedInRoom()) return;
    if (selection.kind === 'room') { this.askRoomAction('remove'); return; }
    if (selection.kind === 'wall') {
      const wall = level.walls.find(value => value.id === selection.id);
      if (!wall) return;
      if (this.state.mode !== 'plan' || this.state.planSurface !== 'floor') { if (isFreeStandingWall(level, wall)) this.deleteSelection(); return; }
      if (!wall.hidden && isContourWall(level, wall)) { this.set({ wallRemoval: wall.id }); return; }
    }
    this.deleteSelection();
  }

  /**
   * «Удалить комнату?» or «Клонировать» about the selected room, asked on the floor plan only, where the app's RemoveRoom
   * and CloneRoom show (`EditorHelper.IsFloor2D`).
   */
  askRoomAction(kind: 'remove' | 'clone'): void {
    const selection = this.state.selection;
    if (selection?.kind !== 'room' || this.refusedInRoom() || this.state.mode !== 'plan' || this.state.planSurface !== 'floor') return;
    if (this.level?.rooms.some(room => room.index === selection.index)) this.set({ roomAction: { kind, index: selection.index } });
  }

  /** The answer: «Удалить» removes the room, «Клонировать всю комнату» or «только стены» copies it, a cancel leaves it. */
  answerRoomAction(answer: 'remove' | 'clone-all' | 'clone-walls' | null): void {
    const action = this.state.roomAction;
    this.set({ roomAction: null });
    if (!action || !answer) return;
    const error = answer === 'remove' ? this.removeRoom(action.index) : this.cloneRoom(action.index, answer === 'clone-all');
    if (error) this.set({ error });
  }

  /** The room with the walls only it stands on and what it holds (`geometry.removeRoom`); nothing stays selected. */
  removeRoom(index: number): string | null {
    const error = this.applyGeometry(level => geometry.removeRoom(level, index));
    if (!error) this.select(null);
    return error;
  }

  /**
   * A copy of the room beside it (`geometry.cloneRoom`), selected, as one undo step. A door or window it brings to a
   * wall another room keeps a copy of gets its copy there, as a placed one does (`mirrorMovedOpenings`).
   */
  cloneRoom(index: number, withFurniture: boolean): string | null {
    const level = this.level;
    if (!level) return t('#StoreNoProject');
    const result = geometry.cloneRoom(level, index, withFurniture);
    if (!result.level) return result.error;
    this.commitLevel('', () => mirrorMovedOpenings(level, result.level), result.created);
    this.select({ kind: 'room', index: result.index });
    return null;
  }

  /**
   * A ready room of the app's shapes added beside the plan (`geometry.addShapedRoom`), selected, the room tool put
   * away, as one undo step. `shape` is the shape project's floor (`roomShapeLevel`).
   */
  addShapedRoom(shape: Level): string | null {
    const level = this.level;
    if (!level) return t('#StoreNoProject');
    const result = geometry.addShapedRoom(level, shape);
    if (!result.level) return result.error;
    this.commitLevel('', () => result.level, result.created);
    this.set({ tool: 'navigate' });
    this.select({ kind: 'room', index: result.index });
    return null;
  }

  /** The answer to that question: «Удалить стену» removes the wall, «Скрыть стену» hides it, a cancel leaves it. */
  answerWallRemoval(answer: 'remove' | 'hide' | null): void {
    const id = this.state.wallRemoval;
    this.set({ wallRemoval: null });
    if (!id || !answer) return;
    if (answer === 'hide') { this.setWallHidden(id, true); return; }
    const error = this.removeWall(id);
    if (error) this.set({ error });
  }

  /** «Скрыть стену» / «Показать стену»: the wall and every room's copy of it. */
  setWallHidden(id: string, hidden: boolean): void {
    this.commitLevel('', level => withWallHidden(level, id, hidden));
  }

  /** «Разделить стену»: a corner where the wall was picked, else in its middle. Nothing stays selected, as in the app. */
  splitWallAt(id: string, point?: Vec2 | null): string | null {
    const error = this.applyGeometry(level => splitWallAt(level, id, point));
    if (!error) this.select(null);
    return error;
  }

  /** «Дублировать» a door, window or opening: a copy beside it on its wall, which becomes the selection. */
  duplicateOpening(id: string): string | null {
    const level = this.level;
    if (!level) return t('#StoreNoProject');
    const result = duplicatedOpening(level, id);
    if ('error' in result) return result.error;
    this.commitLevel('', () => result.level);
    this.select({ kind: 'opening', id: result.id });
    return null;
  }

  /** A door, window or arch across a hidden wall, which comes back to show it. */
  fillHiddenWall(id: string, kind: OpeningKind): string | null {
    const level = this.level;
    if (!level) return t('#StoreNoProject');
    const result = filledHiddenWall(level, id, kind);
    if ('error' in result) return result.error;
    this.commitLevel('', () => result.level);
    this.select({ kind: 'opening', id: result.id });
    return null;
  }

  /** «Выбрать шторы»: the catalog opens on its curtains for this door, window or arch, which stays selected. */
  chooseCurtains(openingId: string): void {
    if (this.refusedInRoom(ONLY_IN_APP)) return;
    this.set({ curtainsFor: openingId, showCatalog: true, catalogSection: null, showProperties: false, showVariations: false, materialTarget: null });
  }

  /**
   * A curtain of `model` hung over the opening in place of the ones it had, or, with null, «Без штор»: its
   * curtains taken down (the app's ReplaceCurtains). One undo step; the opening stays selected.
   */
  setOpeningCurtains(openingId: string, model: CurtainModel | null): string | null {
    const level = this.level, opening = level?.openings.find(value => value.id === openingId);
    if (!level || !opening) return t('#GeometryOpeningNotFound');
    const curtain = model ? hungCurtain(level, opening, model, 'item-' + crypto.randomUUID()) : null;
    if (model && !curtain) return t('#StoreCurtainsDoNotFit');
    const next = withOpeningCurtains(level, openingId, curtain);
    if (next !== level) this.commitLevel('', () => next, curtain ? [curtain.id] : []);
    return null;
  }

  /**
   * «Добавить мансарду» (AddAtticCommand): a mansard along this face of the wall — 1 for its room's face (`normalIn`),
   * −1 for the other — in one undo step; it becomes the selection with its resize open, as the app opens it.
   */
  addAttic(wallId: string, side: 1 | -1 = 1): string | null {
    const level = this.level, wall = level?.walls.find(value => value.id === wallId);
    if (!level || !wall) return t('#GeometryWallNotFound');
    if (this.state.edit === 'items') return translate(ITEMS_ONLY);
    if (!takesAttic(level, wall, side)) return t('#StoreAtticNotPossible');
    const attic = newAttic(level, wall, side, 'item-' + crypto.randomUUID());
    this.commitLevel('', value => ({ ...value, items: [...value.items, attic] }), [attic.id]);
    this.select({ kind: 'item', id: attic.id });
    this.setResizeMode(true);
    return null;
  }

  /** A ceiling molding along the top of the wall's room face, or none. */
  toggleWallMolding(id: string): void {
    const wall = this.level?.walls.find(value => value.id === id);
    if (wall) this.commitLevel('', level => withWallTrim(level, id, 'molding', !wall.molding));
  }

  /** Ceiling moldings along every wall of the room, or none once any has one. */
  toggleRoomMoldings(index: number): void {
    this.commitLevel('', level => withRoomTrims(level, index, 'molding'));
  }

  /** A plinth along the wall's room face, or none. */
  toggleWallPlinth(id: string): void {
    const wall = this.level?.walls.find(value => value.id === id);
    if (wall) this.commitLevel('', level => withWallPlinth(level, id, !wall.plinth));
  }

  /** Plinths along every wall of the room, or none once any has one. */
  toggleRoomPlinths(index: number): void {
    this.commitLevel('', level => withRoomPlinths(level, index));
  }
}

function updateItem(level: Level, id: string, update: (item: Item) => Item): Level {
  const index = level.items.findIndex(item => item.id === id);
  if (index < 0) return level;
  const next = update(level.items[index]);
  if (next === level.items[index]) return level;
  const items = level.items.slice(); items[index] = next;
  return { ...level, items };
}
/** The pieces `ids`, in the floor's order, then whatever stands on each of them (`stackedItems`, the set a move carries), each with the picked piece it goes with. */
function withRiders(items: readonly Item[], ids: readonly string[], probe?: SurfaceProbe): Array<{ item: Item; base: Item }> {
  const picked = items.filter(item => ids.includes(item.id)), taken = new Set(picked.map(item => item.id)), riders: Array<{ item: Item; base: Item }> = [];
  for (const base of picked) {
    for (const rider of stackedItems(items, base, probe)) {
      if (!taken.has(rider.id)) { taken.add(rider.id); riders.push({ item: rider, base }); }
    }
  }
  return [...picked.map(item => ({ item, base: item })), ...riders];
}
/**
 * Where a copy goes from its source: along a wall piece's wall, so it stays on that face, and no further than the wall
 * has room on that side, so it stays within the wall — but never less than the app's least shift, toward the larger
 * gap; else across the floor.
 */
function copyShift(level: Level, item: Item): Vec2 {
  const wall = item.placement === 'wall' ? level.walls.find(value => value.id === item.wallId) : undefined;
  if (!wall) return { x: COPY_SHIFT, z: COPY_SHIFT };
  const along = (item.position.x - wall.center.x) * wall.dir.x + (item.position.z - wall.center.z) * wall.dir.z;
  const ahead = wall.length / 2 - item.size.w / 2 - along, behind = wall.length / 2 - item.size.w / 2 + along;
  const sign = ahead >= COPY_SHIFT ? 1 : behind >= COPY_SHIFT ? -1 : ahead >= behind ? 1 : -1;
  const shift = sign * Math.max(COPY_MIN_SHIFT, Math.min(COPY_SHIFT, sign > 0 ? ahead : behind));
  return { x: wall.dir.x * shift, z: wall.dir.z * shift };
}
/** Whether `id`'s place, turn or size differs between the two floors. */
function placeChanged(before: Level, after: Level, id: string): boolean {
  const was = before.items.find(item => item.id === id), now = after.items.find(item => item.id === id);
  return !was || !now || was.position.x !== now.position.x || was.position.z !== now.position.z || was.rotation !== now.rotation
    || was.elevation !== now.elevation || was.size.w !== now.size.w || was.size.h !== now.size.h || was.size.d !== now.size.d;
}
/** Why the app's live room, where the web changes items alone, cannot take `patch`; null when it can. */
function itemsOnlyRefusal(patch: DocumentPatch): string | null {
  if (patch.kind === 'replace' || patch.floors.some(floor => floor.architectureAfter)) return ITEMS_ONLY;
  if (patch.meta || patch.name) return ONLY_IN_APP;
  for (const { before, after } of patch.floors.flatMap(floor => floor.items)) {
    const refusal = before && after ? livePieceRefusal(before, after) : null;
    if (refusal) return refusal;
  }
  return null;
}
function round(v: number): number { return Math.round(v * 1e4) / 1e4; }
/** `level` with `surface` on `target` (`all` as `Store.setSurface` reads it); `level` itself when nothing changed. */
function surfaceEdit(level: Level, target: NonNullable<MaterialTarget>, surface: Surface, all: boolean | 'room'): Level {
  if (target.kind === 'item') return updateItem(level, target.itemId, item => {
    const next = structuredClone(item);
    if (target.slot === '*') paintWholeItem(next, surface); else paintItemSlot(next, target.slot, surface);
    return JSON.stringify(next) === JSON.stringify(item) ? item : next;
  });
  if (target.kind === 'opening') return updateOpeningMaterials(level, target.openingId, opening => {
    if (target.slot === '*') paintWholeItem(opening, surface); else paintItemSlot(opening, target.slot, surface);
  });
  if (target.kind === 'trim') {
    // One plinth or molding, or with «all» every one of its kind in the room (ApplyMaterialToPlinthOrCeilingMolding).
    const ids = new Set(all ? trimRoomWalls(level, target.wallId).map(wall => wall.id) : [target.wallId]);
    let changed = false;
    const walls = level.walls.map(wall => {
      const trim = wall[target.trim];
      if (!ids.has(wall.id) || !trim || JSON.stringify(trim.surface) === JSON.stringify(surface)) return wall;
      changed = true; return { ...wall, [target.trim]: { ...trim, surface } };
    });
    return changed ? { ...level, walls } : level;
  }
  const surfaceKey = JSON.stringify(surface);
  if (target.kind === 'wall') {
    const faces = new Map((all === 'room' ? connectedRoomFaces(level, target.wallId, target.side)
      : all ? level.walls.map(wall => ({ wallId: wall.id, side: target.side })) : [{ wallId: target.wallId, side: target.side }]).map(face => [face.wallId, face.side]));
    let changed = false;
    const walls = level.walls.map(wall => {
      const side = faces.get(wall.id), key = side === 'back' ? 'backSurface' : 'frontSurface';
      if (!side || JSON.stringify(wall[key]) === surfaceKey) return wall;
      changed = true; return { ...wall, [key]: surface };
    });
    return changed ? { ...level, walls } : level;
  }
  const key = target.kind === 'floor' ? 'floorSurface' : 'ceilingSurface';
  let changed = false;
  const rooms = level.rooms.map(room => {
    if ((!all && room.index !== target.roomIndex) || JSON.stringify(room[key]) === surfaceKey) return room;
    changed = true; return { ...room, [key]: surface };
  });
  return changed ? { ...level, rooms } : level;
}
/**
 * `level` with the materials of the opening `id` edited by `edit` on a copy, its copy on the other room's copy of the
 * wall dressed alike (`syncOpeningTwins`), as the app keeps the door between two rooms one door; `level` itself when
 * nothing changed.
 */
function updateOpeningMaterials(level: Level, id: string, edit: (opening: Opening) => void): Level {
  const opening = level.openings.find(value => value.id === id);
  if (!opening) return level;
  const next = structuredClone(opening);
  edit(next);
  if (JSON.stringify(next) === JSON.stringify(opening)) return level;
  return syncOpeningTwins(level, { ...level, openings: level.openings.map(value => value.id === id ? next : value) }, [id]);
}
/** A deliberate whole-item edit overrides imported per-slot finishes at every depth. */
function paintWholeItem(item: Pick<Item, 'materials' | 'modules'>, surface: Surface): void {
  item.materials = Object.fromEntries([...Object.keys(item.materials ?? {}), '*'].map(slot => [slot, surface]));
  for (const module of item.modules ?? []) paintWholeItem(module, surface);
}
/** A named user edit wins over inherited module overrides of that same native slot. */
function paintItemSlot(item: Pick<Item,'materials'|'modules'>, slot:string, surface:Surface, root=true):void {
  const normalize=(value:string)=>value.replace(/\s*\(Instance\)\s*$/i,'').trim().toLowerCase();
  const matching=Object.keys(item.materials??{}).filter(key=>normalize(key)===normalize(slot));
  if(root||matching.length){
    item.materials={...item.materials};
    for(const key of matching)item.materials[key]=surface;
    if(root)item.materials[slot]=surface;
  }
  for(const module of item.modules??[])paintItemSlot(module,slot,surface,false);
}

/** UV gestures preserve each finish. Resolve inherited module overrides from the
 * untouched tree, then write per-slot transforms so wildcard precedence stays intact. */
function transformItemMaterials(item:Pick<Item,'materials'|'modules'>, selected:string, baseline:Surface, draft:Surface, captured:MaterialSlotInfo[]):void {
  const key=(name:string)=>name.replace(/\s*\(Instance\)\s*$/i,'').trim().toLowerCase();
  const slots=new Map(captured.filter(slot=>slot.hasTexture).map(slot=>[key(slot.key),slot]));
  const collect=(node:Pick<Item,'materials'|'modules'>)=>{
    for(const [name,surface] of Object.entries(node.materials??{})){
      if(!slots.has(key(name))&&(surface.texture||surface.customTexture||(!surface.color&&(surface.tileMeters||surface.tiling))))
        slots.set(key(name),{key:name,label:name,surface,hasTexture:true,weight:0});
    }
    node.modules?.forEach(collect);
  };collect(item);
  if(selected!=='*'&&!slots.has(key(selected)))slots.set(key(selected),{key:selected,label:selected,surface:baseline,hasTexture:true,weight:0});
  const chosen=[...slots.values()].filter(slot=>selected==='*'||key(slot.key)===key(selected));
  const walk=(node:Pick<Item,'materials'|'modules'>,inherited:Record<string,Surface>)=>{
    const effective={...inherited,...node.materials};
    for(const slot of chosen){
      const matching=Object.entries(effective).reverse().find(([name])=>key(name)===key(slot.key));
      const existing=matching?.[1]??effective['*'];
      if(existing?.color&&!existing.texture&&!existing.customTexture)continue;
      // Embedded defaults need only UV fields: no texture replacement or PBR reset.
      const uv={tileMeters:slot.surface.tileMeters,tiling:slot.surface.tiling,offset:slot.surface.offset,rotation:slot.surface.rotation};
      const source=existing?(existing.texture||existing.customTexture?existing:{...uv,...existing}):uv;
      node.materials={...node.materials,[slot.key]:withTextureTransformDelta(source,baseline,draft)};
    }
    for(const module of node.modules??[])walk(module,effective);
  };walk(item,{});
}
function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
function snapshotKey(projectId: string): string { return `hp.scene.${projectId}`; }

export const store = new Store();

// A console handle on the editor state. The plan and the scene are both canvases, so
// without this the only way to check what is selected or where something moved is to
// look at a picture — which is exactly how placement bugs survive.
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  (window as unknown as { hpStore?: Store }).hpStore = store;
}

// i18n-ignore: words matched in level names, saved in either language
const LEGACY_BASEMENT = /подвал|basement|цоколь/i;
/** A browser copy saved before 4 November 2026 has no `lowerLevel`: its basements are known, once, by their stored name. */
function withBasementsSettled(scene: Scene): Scene {
  if (scene.levels.every(level => typeof level.lowerLevel === 'boolean')) return scene;
  return { ...scene, levels: scene.levels.map(level => typeof level.lowerLevel === 'boolean' ? level : { ...level, lowerLevel: LEGACY_BASEMENT.test(level.name) }) };
}
/** `Level.index` is the level's place in the array; a moved or added level renumbers the rest. */
function reindexed(levels: Level[]): Level[] { return levels.map((level, i) => level.index === i ? level : { ...level, index: i }); }

/**
 * `scene` with the floor the app's AddLevel.CreateLevel adds: a copy of the top floor over it, or of the lowest one
 * under it (under the roof floor, AddLevel.UpperInsertIndex), cleared of furniture (`staysOnNewFloor`), with no name of its own; and the
 * index it takes.
 */
function withLevel(scene: Scene, where: 'above' | 'basement'): { scene: Scene; index: number } {
  const below = where === 'basement', at = below ? 0 : upperInsertIndex(scene.levels);
  const source = below ? scene.levels[0] : scene.levels[Math.max(0, at - 1)];
  const level = { ...withoutRoofRooms(copyFloor(source, '', staysOnNewFloor)), lowerLevel: below };
  const levels = reindexed([...scene.levels.slice(0, at), level, ...scene.levels.slice(at)]);
  return { scene: { ...scene, levels }, index: at };
}
